/* ========================================================================== *\
___________       __         .__
\_   _____/ _____/  |________|__| ____
 |    __)_ /    \   __\_  __ \  |/  _ \
 |        \   |  \  |  |  | \/  (  <_> )
/_______  /___|  /__|  |__|  |__|\____/
        \/     \/
\* ========================================================================== */

/*
 * CampaignGraphic.jsx — กราฟิกแคมเปญด้วย AI   (งานข้อ 6.5)
 *
 * โครงและดีไซน์เดียวกับ ProductAiImage.jsx (งานข้อ 4) — ถือ state ตัวเอง
 * ต่อเข้า Campaign.jsx ด้วย 3 บรรทัด ไม่แตะฟอร์มแคมเปญเดิม (1,943 บรรทัด ห้าม refactor)
 *
 * ต่างจากฝั่งสินค้า:
 *   - ผูกกับ campaign_id · โมเดลตั้งต้น = GPT Image 2 (เด่นตัวหนังสือไทย — ผลทดสอบ 1-2 ก.ย.)
 *   - มีปุ่ม "เติมข้อความจากข้อมูลแคมเปญ" สร้าง prompt จากชื่อ/โปรโมชัน/ช่วงเวลาจริง
 *   - อนุมัติ = คัดลอกเข้า campaigns_image (รูปแคมเปญปกติ)
 */

import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';

// @mui material components
import Grid from '@mui/material/Grid';
import Icon from '@mui/material/Icon';
import MenuItem from '@mui/material/MenuItem';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import Card from '@mui/material/Card';
import ButtonBase from '@mui/material/ButtonBase';
import Switch from '@mui/material/Switch';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDProgress from 'components/MDProgress';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import MDSnackbar from 'components/MDSnackbar';
import ImageReview from 'layouts/_entrio/ImageReview';
import TemplateManager from 'layouts/_entrio/TemplateManager';
import LogoPlacer, { logoBox, presetXY, ratioHW } from 'layouts/_entrio/LogoPlacer';

import Resizer from 'react-image-file-resizer';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';
import promptLength from 'layouts/_entrio/promptLength';
import {
  ReviewThumb,
  CardMenu,
  ActionRow,
  DownloadButton,
  SelectBar,
  iconBtnSx,
} from 'layouts/_entrio/ResultCardParts';
import useBulkDownload from 'layouts/_entrio/useBulkDownload';

/* เครดิตต่อภาพตามความละเอียดที่เลือก (migration 0024 · backend ส่ง credits = {1K,2K,4K})
   ยังไม่รัน 0024 → ไม่มี credits ใช้ credits_per_image แทน (เจ้าของงานทัก 6 ต.ค. 2569 ว่าเปลี่ยน 4K แล้วตัวเลขไม่ขยับ) */
const creditOf = (p, res) => {
  if (!p) return 0;
  const map = p.credits || {};
  return Number(map[res] != null ? map[res] : p.credits_per_image) || 0;
};

/* ร่างของแผง (ข้อความ · สไตล์ · สัดส่วน/ความละเอียด/โมเดล · คำสั่งที่แก้เอง) เก็บ sessionStorage ต่อผู้ใช้+แคมเปญ อายุ 8 ชม.
   สลับแท็บแล้วไม่หาย · ไม่เก็บรูปอัปโหลด · ส่งงานสำเร็จแล้วลบ (16 ต.ค. 2569 · แบบเดียวกับ ProductAiInfo) */
const DRAFT_TTL = 8 * 60 * 60 * 1000;
const CURRENT_LOGO = '__current';
const RESERVE_WHERE = {
  tl: 'มุมบนซ้าย',
  tc: 'กึ่งกลางด้านบน',
  tr: 'มุมบนขวา',
  bl: 'มุมล่างซ้าย',
  bc: 'กึ่งกลางด้านล่าง',
  br: 'มุมล่างขวา',
}; // ตัวเลือก "โลโก้ที่ใช้อยู่" ในหน้าต่างวางโลโก้ (ไม่ส่ง logo = server ใช้โลโก้เดิมของแถว)
/* ประโยคผังตำแหน่งโลโก้ — ตรงกับ layoutSentence ฝั่ง server ทุกตัวอักษร (ขนาด % ของด้านสั้น → % ของความกว้างตามสัดส่วน)
   26 ต.ค. 2569: แนบกระดาษที่วางโลโก้ไว้ให้ AI เจนลงในงาน แทนการวางไฟล์ทับหลังเจน (เจ้าของงาน: "เหมือนแปะเฉยๆ ไม่ผ่าน") */
/* logoAR = สูง/กว้าง ของไฟล์โลโก้ — ความสูงจริงของกล่องโลโก้ (ตรงกับ layoutSentence ฝั่ง server) */
const layoutText = (pos, xy, size, ratio, logoAR = 1) => {
  const ir = ratioHW(ratio);
  const w = Number(size) * Math.min(1, ir);
  const h = Number(size) * Math.min(1, 1 / ir) * (Number(logoAR) > 0 ? Number(logoAR) : 1);
  let where = RESERVE_WHERE[pos];
  if (pos === 'custom') {
    const cx = Number(xy[0]) + w / 2;
    const cy = Number(xy[1]) + h / 2;
    where = `บริเวณ${cy < 33 ? 'ด้านบน' : cy > 66 ? 'ด้านล่าง' : 'กลางภาพ'}ฝั่ง${
      cx < 33 ? 'ซ้าย' : cx > 66 ? 'ขวา' : 'กลาง'
    }`;
  }
  if (!where) return '';
  return `รูปที่แนบรูปสุดท้ายคือผังตำแหน่งโลโก้ร้าน ใส่โลโก้นี้ลงในภาพ${where} ตำแหน่งและขนาดตรงกับผัง กว้างประมาณ ${Math.round(
    w
  )}% ของความกว้างภาพ คงรูปทรง ตัวอักษร และสีของโลโก้ทุกประการ ห้ามวาดหรือแต่งตัวโลโก้ใหม่ แต่ให้โลโก้กลมกลืนไปกับฉากหลังและเป็นส่วนหนึ่งของงานกราฟิก เช่น มีพื้นรอง กรอบ หรือลายกราฟิกรอบโลโก้ที่เข้ากับธีมและสีของภาพ แสงเงาเข้ากับฉาก ไม่ให้ดูเหมือนแปะสติกเกอร์ ไม่ต้องใช้พื้นสีเทาของผัง และห้ามวางข้อความ ราคา หรือสินค้าทับโลโก้`;
};

/* ปุ่มแก้สำเร็จรูปใน dialog "แก้ไข" — ข้อความที่พิสูจน์แล้วว่า GPT i2i เข้าใจ · คงองค์ประกอบอื่นไว้เสมอ */
const QUICK_FIXES = [
  {
    label: 'ตัวหนังสือผิด ลองใหม่',
    text: 'แก้ตัวอักษรไทยให้ถูกต้องคมชัดทุกตัว วรรณยุกต์ถูกตำแหน่ง คงองค์ประกอบอื่นเหมือนเดิม',
  },
  {
    label: 'ตัวอักษรใหญ่ขึ้น',
    text: 'ขยายข้อความหลักให้ใหญ่ขึ้นอ่านชัดจากระยะไกล คงองค์ประกอบอื่นเหมือนเดิม',
  },
  { label: 'สินค้าใหญ่ขึ้น', text: 'ขยายสินค้าให้ใหญ่และเด่นขึ้น คงฉลากและสีสินค้าเหมือนเดิม' },
  { label: 'เอาคนออก', text: 'เอาคนและมือออกจากภาพ คงสินค้าและข้อความเหมือนเดิม' },
  { label: 'พื้นหลังสีขาว', text: 'เปลี่ยนพื้นหลังเป็นสีขาวสะอาด คงสินค้าและข้อความเหมือนเดิม' },
  { label: 'พื้นหลังสีฟ้า', text: 'เปลี่ยนพื้นหลังเป็นสีฟ้าสดใส คงสินค้าและข้อความเหมือนเดิม' },
  {
    label: 'พื้นหลังสีแดง-เหลือง',
    text: 'เปลี่ยนพื้นหลังเป็นโทนแดง-เหลืองแบบป้ายลดราคา คงสินค้าและข้อความเหมือนเดิม',
  },
  { label: 'สว่างขึ้น', text: 'ปรับภาพให้สว่างและสดใสขึ้น คงองค์ประกอบเหมือนเดิม' },
  { label: 'ลดของตกแต่ง', text: 'ลดของตกแต่งรอบ ๆ ให้ภาพสะอาดตาขึ้น เน้นสินค้าและข้อความ' },
];

const statusList = {
  PENDING: { name: 'กำลังสร้าง', color: 'warning' },
  RUNNING: { name: 'กำลังสร้าง', color: 'warning' },
  SUCCESS: { name: 'สำเร็จ', color: 'success' },
  FAILED: { name: 'ไม่สำเร็จ', color: 'error' },
  CANCELLED: { name: 'ยกเลิก', color: 'secondary' },
};

/* สัดส่วนภาพ = สื่อที่จะเอาไปลง (ไม่ใช่สไตล์) — ชื่อกำกับในช่องเลือก + คำแนะนำจัดวางที่ระบบเติมท้ายคำสั่งให้เอง (6 ต.ค. 2569) */
const RATIO_LABELS = {
  '1:1': 'โพสต์ / LINE',
  '4:5': 'โพสต์แนวตั้ง',
  '9:16': 'สตอรี่ / Reels',
  '16:9': 'ปกเพจ / จอแนวนอน',
  '3:4': 'ป้ายตั้งหน้าร้าน',
  '3:2': 'ป้ายชั้นวาง',
  '2:3': 'โปสเตอร์แนวตั้ง',
  '4:3': 'จอแนวนอน',
  '21:9': 'แบนเนอร์ยาว',
};
const RATIO_HINTS = {
  '4:5': 'ภาพแนวตั้ง ข้อความหลักอยู่ส่วนบน สินค้าอยู่กลางถึงล่าง',
  '9:16':
    'ภาพแนวตั้งสำหรับสตอรี่ ข้อความหลักอยู่ส่วนบน สินค้าอยู่กลางถึงล่าง เว้นขอบบนและล่างไว้สำหรับปุ่มของแอป',
  '16:9':
    'ภาพแนวนอน ข้อความหลักด้านซ้าย สินค้าเรียงด้านขวา เว้นมุมล่างซ้ายไว้ไม่ให้โดนรูปโปรไฟล์บัง',
  '3:4': 'ป้ายแนวตั้งสำหรับพิมพ์ตั้งหน้าร้าน ข้อความหลักและตัวเลขราคาใหญ่มากอ่านได้จากระยะ 3 เมตร',
  '3:2': 'ป้ายเล็กแนวนอนติดชั้นวาง ข้อความสั้นมาก ตัวเลขราคาใหญ่ที่สุด',
  '2:3': 'โปสเตอร์แนวตั้ง ข้อความหลักส่วนบน สินค้าส่วนล่าง',
  '21:9': 'แบนเนอร์แนวนอนยาว ข้อความและสินค้าเรียงในแนวนอน',
};

function CampaignGraphic({ onSaved, onOpenImages }) {
  const mod = 'campaign_graphic';
  const app = useSelector((state) => state[APP_STATE]);
  const { t } = useTranslation();
  const { id } = useParams();

  const [prompt, setPrompt] = useState('');
  const [mode, setMode] = useState('none');
  const [resolution, setResolution] = useState('1K');
  const [ratio, setRatio] = useState('1:1');
  const [provider, setProvider] = useState('auto'); // auto = โมเดลตั้งต้นของโหมดนั้น (backend เลือกให้)
  const [attachList, setAttachList] = useState([]); // base64 data URL ของรูปที่ผู้ใช้แนบ (โหมด upload) — แนบได้หลายรูป สูงสุด meta.max_attach
  const fileRef = useRef(null);
  const [editProvider, setEditProvider] = useState('auto');
  const [editRatio, setEditRatio] = useState('1:1'); // ของการ์ดที่กดแก้ — แผงซ้ายอาจคนละค่า (รีเช็ค 27 ต.ค. 2569 · หน้าภาพสินค้าทำไว้แล้ว 18 ต.ค.)
  const [editResolution, setEditResolution] = useState('1K');
  /* แก้ไขภาพ + แนบรูปเพิ่ม (25 ต.ค. 2569 · ลูกค้าเจอในประชุมว่าแนบไม่ได้) — รูปสินค้าในแคมเปญที่ติ๊ก + รูปอัปโหลด ส่งไปพร้อมภาพเดิม */
  const [editProductSel, setEditProductSel] = useState([]);
  const [editAttach, setEditAttach] = useState([]);
  const [editAttaching, setEditAttaching] = useState(false);
  const [pickerTarget, setPickerTarget] = useState('main'); // dialog เลือกสินค้าในแคมเปญใช้ร่วมแผงหลัก/หน้าต่างแก้ไข
  const editFileRef = useRef(null);

  const [meta, setMeta] = useState({
    providers: [],
    resolutions: [],
    ratios: [],
    presets: [],
    preset_usage: {},
    logos: [],
    logo_prompt: '',
    mascots: [],
    mascot_prompt: '',
    supports_logo_overlay: 'N',
    supports_logo_layout: 'N',
    logo_positions: [],
    logo_sizes: [],
    can_generate: '', // '' = ยังโหลดไม่เสร็จ — ไม่โชว์ทั้งฟอร์มและป้ายดูอย่างเดียว กันกระพริบ (18 ต.ค. 2569)
    usage: null,
  });
  /* ข้อ 6 (มติประชุมลูกค้า 5 ต.ค. 2569) — สไตล์สำเร็จรูปที่เลือกอยู่ ('' = พิมพ์เอง) และหมวดที่กรอง ('' = ทั้งหมด) */
  const [presetCode, setPresetCode] = useState('');
  const [presetCat, setPresetCat] = useState('');
  /* ฟอร์มกรอกง่าย ๆ สำหรับคนที่ไม่ถนัด AI (เจ้าของงานขอ 5 ต.ค. 2569) — ระบบประกอบคำสั่งให้เบื้องหลัง
     advanced = เปิดช่องคำสั่งเต็มให้แก้เอง (ซ่อนไว้เป็นค่าตั้งต้น) */
  const [advanced, setAdvanced] = useState(false);
  const [styleOpen, setStyleOpen] = useState(false);
  const [promptOpen, setPromptOpen] = useState(false);
  const [promptSnapshot, setPromptSnapshot] = useState('');
  const [promptReset, setPromptReset] = useState(false);
  const [productOpen, setProductOpen] = useState(false);
  const [attaching, setAttaching] = useState(false);
  /* คลังสไตล์ใน dialog: โชว์ยอดนิยมก่อน · โมเดล/สัดส่วนอยู่ในแผงหลักเสมอ */
  const [showAll, setShowAll] = useState(false);
  const [productSel, setProductSel] = useState([]);
  /* โลโก้ร้าน (5 ต.ค. 2569) — แนบเป็นรูปต้นแบบรูปสุดท้ายให้ AI ใช้ของจริง ไม่แต่งโลโก้เอง · เปิดเป็นค่าตั้งต้น */
  const [logoOn, setLogoOn] = useState(true);
  /* ตำแหน่งโลโก้ (A3 · 25 ต.ค. 2569): tl/tc/tr/bl/bc/br = server วางไฟล์จริงทับหลังเจน (ไม่ส่งให้ AI) · 'ai' = แบบเดิมให้ AI วาด */
  const [logoPos, setLogoPos] = useState('tl');
  const [logoSize, setLogoSize] = useState(18);
  const [logoXY, setLogoXY] = useState([3, 3]); // ตำแหน่ง "กำหนดเอง" เป็น % มุมบนซ้าย (เลือกบนกระดาษก่อนสร้าง)
  const [logoAR, setLogoAR] = useState(1); // สูง/กว้าง ของไฟล์โลโก้ (รู้ตอนรูปย่อโหลด)
  const [paperOpen, setPaperOpen] = useState(false); // หน้าต่างกระดาษเลือกจุดวางโลโก้
  /* เปลี่ยนสัดส่วน/ขนาดแล้วจุดที่เลือกไว้อาจล้นกระดาษ — ดึงกลับเข้าในเหมือนที่ server ล็อกขอบตอนวางจริง */
  const paperBox = logoBox(Number(logoSize), logoAR, ratioHW(ratio));
  const logoXYc = [
    Math.max(0, Math.min(100 - paperBox.w, logoXY[0])),
    Math.max(0, Math.min(100 - paperBox.h, logoXY[1])),
  ];
  const [placeRow, setPlaceRow] = useState(null); // แถวที่เปิดหน้าต่างวาง/ย้ายโลโก้
  const [placing, setPlacing] = useState(false);
  /* ผลงานเห็นเฉพาะของตัวเอง · ผู้ดูแล AI (ai_setting) เปิด "ดูของทุกคน" ได้ (27 ต.ค. 2569 · คนเป็นพันเจนพร้อมกัน 50 ภาพล่าสุดจะดันของเราหาย) */
  const [canViewAll, setCanViewAll] = useState(false);
  const [listReady, setListReady] = useState(false); // ยังไม่รู้สิทธิ์จนรายการมาถึง — กันข้อความ "เห็นเฉพาะของตัวเอง" แวบขึ้นมา
  const [scopeAll, setScopeAll] = useState(false);
  const scopeRef = useRef(false); // poll เรียก loadList จาก closure เก่า — อ่านค่าล่าสุดจาก ref
  /* มาสคอตช้างแดงของร้าน (A4 · 25 ต.ค. 2569) — แนบเป็นรูปต้นแบบเหมือนโลโก้ · ค่าตั้งต้นปิด เปิดเองเมื่อเลือกสไตล์มาสคอต */
  const [mascotOn, setMascotOn] = useState(false);
  const [mascotCode, setMascotCode] = useState('');
  const [logoCode, setLogoCode] = useState(''); // product_id ที่ติ๊กไว้ในโหมดรูปสินค้าในแคมเปญ (ตั้งต้น = ทุกตัว)
  const initializedCampaign = useRef(null);
  const activeCampaign = useRef(id);
  const [resetConfirm, setResetConfirm] = useState(false);
  const [form, setForm] = useState({ headline: '', sub: '', products: '', period: 'Y', extra: '' });
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0); // ทั้งหมดที่มี · แสดงทีละ 50 กด "แสดงเพิ่ม" (ตรวจการใช้งาน 1.8 · 1 พ.ย. 2569)
  const limitRef = useRef(50);
  const [reviewId, setReviewId] = useState(null);
  const [campaign, setCampaign] = useState(null); // ข้อมูลแคมเปญไว้เติม prompt
  const [notify, setNotify] = useState(null);
  const [loading, setLoading] = useState(true);

  const [editRow, setEditRow] = useState(null); // แถวที่กด "แก้ไข" — เปิด dialog prompt ใหม่
  const [editPrompt, setEditPrompt] = useState('');
  const [delRow, setDelRow] = useState(null); // แถวที่กด "ลบ" — เปิด dialog ยืนยัน
  const [retryTick, setRetryTick] = useState(0); // กด "ลองใหม่" บนการ์ดที่ไม่สำเร็จ

  const draftKey = `entrio:campaign-graphic:v1:${app.user.id}:${id}`;
  const draftLoaded = useRef(''); // draftKey ที่กู้แล้ว — ก่อนกู้ห้ามเขียนทับร่าง
  const draftProvider = useRef(''); // โมเดลจากร่างที่รอตั้ง (ดู effect กู้โมเดล)
  const draftCleared = useRef(''); // ค่าตอนส่งงานสำเร็จ — ยังไม่แก้อะไรเพิ่มก็ไม่เก็บร่างใหม่
  const resnapAfterSubmit = useRef(false);
  const polling = useRef(false); // poll รอบก่อนยังไม่กลับ
  const notifyTimer = useRef(null);

  /* กัน setState หลัง unmount ระหว่างรอ poll */
  const alive = useRef(true);
  /* ตั้ง true ตอน mount ด้วย — ไม่งั้น Fast Refresh (แก้ไฟล์ขณะหน้าเปิด) รัน cleanup แล้ว alive ค้าง false
     ทำให้ .then ของทุก request ถูกข้าม หน้าค้างที่ overlay (เจอ 5 ต.ค. ตอนเดโม) */
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const showNotify = (msg, color) => {
    setNotify(
      <MDSnackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }} // มุมล่างขวาบังปุ่มปิดหน้าต่างตรวจภาพ
        color={color}
        icon='notifications'
        title={t(`app.name`)}
        content={msg}
        dateTime={Func.dateTimeDisplay()}
        open={Boolean(true)}
        close={() => setNotify(null)}
      />
    );

    /* error ค้าง 8 วิ ให้อ่านทัน · ล้างตัวจับเวลาเก่า ไม่งั้นข้อความก่อนหน้าปิดข้อความใหม่ก่อนเวลา (18 ต.ค. 2569) */
    clearTimeout(notifyTimer.current);
    notifyTimer.current = setTimeout(
      function () {
        if (alive.current) setNotify(null);
      },
      color === 'error' ? 8000 : 3000
    );
  };

  /* ดาวน์โหลดภาพ ทีละภาพ / ติ๊กหลายภาพเป็น zip (A2 · 25 ต.ค. 2569) · ชื่อไฟล์ = รหัสแคมเปญ_เลขงาน */
  const dl = useBulkDownload();
  const dlName = (campaign && campaign.code) || `แคมเปญ${id}`;
  const dlItem = (row) => ({ id: row.id, url: row.image_url, name: `${dlName}_${row.id}` });
  const dlAll = list.filter((row) => row.image_url).map(dlItem);

  const loadMeta = () => {
    const requestedId = id;
    FetchData('GET', `/${mod}`, app.token, {})
      .then((result) => {
        if (result.status === 'ok' && alive.current && activeCampaign.current === requestedId) {
          setMeta(result);
          const logos = result.logos || [];
          if (logos.length) {
            const branch = logos.find((l) => l.code === 'branch');
            setLogoCode((current) =>
              logos.some((logo) => logo.code === current) ? current : (branch || logos[0]).code
            );
          }
          const mascots = result.mascots || [];
          if (mascots.length) {
            setMascotCode((current) =>
              mascots.some((m) => m.code === current) ? current : mascots[0].code
            );
          }
        }
      })
      .catch(() => {
        if (alive.current) showNotify(t(`alert.loaderror`), 'error');
      });
  };

  const loadList = () => {
    const requestedId = id;
    FetchData('OPTIONS', `/${mod}/by_campaign`, app.token, {
      campaign_id: id,
      scope: scopeRef.current ? 'all' : 'mine',
      limit: limitRef.current,
      _t: Date.now(),
    })
      .then((result) => {
        if (!alive.current || activeCampaign.current !== requestedId) return;
        /* สลับ "ดูของทุกคน" ระหว่างคำขอค้าง — คำตอบของมุมมองเดิมมาถึงทีหลัง ห้ามทับรายการ (รีเช็ค 27 ต.ค. 2569) */
        if (result.status === 'ok' && (result.scope === 'all') !== scopeRef.current) return;
        setLoading(false);

        if (result.status === 'ok') {
          setList(result.data || []);
          setTotal(Number(result.total) || 0);
          setCanViewAll(result.can_view_all === 'Y');
          setListReady(true);
          if (result.campaign) setCampaign(result.campaign);
          if (result.usage) setMeta((m) => ({ ...m, usage: result.usage }));
        } else {
          showNotify(t(`alert.loaderror`), 'error');
        }
      })
      .catch(() => {
        if (!alive.current || activeCampaign.current !== requestedId) return;
        setLoading(false);
        setListReady(true);
        showNotify(t(`alert.loaderror`), 'error');
      });
  };

  useEffect(() => {
    if (id) {
      activeCampaign.current = id;
      initializedCampaign.current = null;
      draftProvider.current = '';
      setCampaign(null);
      setList([]);
      setListReady(false);
      setProductSel([]);
      setPrompt('');
      setAdvanced(false);
      setPresetCode('');
      setResetConfirm(false);
      setAttachList([]);
      setReviewId(null);
      setPromptSnapshot('');
      setStyleOpen(false);
      setPromptOpen(false);
      setProductOpen(false);
      setLoading(true);
      loadMeta();
      loadList();
    }
  }, [id]);

  /* ตัวเลือกโมเดลขึ้นกับโหมด — สร้างใหม่ = IMAGE_GEN · แก้จากภาพ = IMAGE_EDIT */
  /* โลโก้แบบวางทับไม่ถูกส่งให้ AI — ไม่นับในเพดานรูป ไม่บังคับโมเดลแก้ภาพ ไม่ต่อคำสั่งโลโก้ */
  /* โลโก้แนบให้ AI เจนลงในภาพเสมอ · logoLayout = แนบเป็นผังตำแหน่ง (6 จุด/กำหนดเอง) · backend เก่า = ไฟล์โลโก้ + LOGO_PROMPT */
  const logoLayout = meta.supports_logo_layout === 'Y' && logoPos !== 'ai';
  const hasLogo = logoOn && Boolean(logoCode);
  const hasMascot = mascotOn && Boolean(mascotCode);
  const providerKind = mode === 'none' && !hasLogo && !hasMascot ? 'IMAGE_GEN' : 'IMAGE_EDIT';
  const providerOptions = meta.providers.filter((p) => p.kind === providerKind);
  const editProviderOptions = meta.providers.filter((p) => p.kind === 'IMAGE_EDIT');
  const editPicked = editProviderOptions.find(
    (p) => p.code === (editProvider === 'auto' ? meta.default_edit : editProvider)
  );
  const editCredit = editPicked ? creditOf(editPicked, editResolution) : null;
  const defaultProviderCode = providerKind === 'IMAGE_GEN' ? meta.default_gen : meta.default_edit;
  const providerPicked = providerOptions.find(
    (p) => p.code === (provider === 'auto' ? defaultProviderCode : provider)
  );

  useEffect(() => {
    if (provider !== 'auto' && !providerOptions.some((p) => p.code === provider))
      setProvider('auto');
  }, [providerKind, meta.providers, provider]);

  /* เปลี่ยนโหมดแล้วรีเซ็ตเป็นโมเดลตั้งต้น กันส่งโมเดลผิดชนิดไป backend */
  useEffect(() => {
    setProvider('auto');
  }, [mode]);

  /* กู้โมเดลจากร่าง — 2 effect ด้านบนรีเซ็ตเป็น auto ระหว่างเปิดหน้า (โหมดเพิ่งตั้ง · รายการโมเดล/โลโก้ยังไม่มา)
     จึงตั้งซ้ำหลังเข้าที่ · ใช้กับโหมดตอนนี้ไม่ได้ = ใช้ auto (16 ต.ค. 2569) */
  useEffect(() => {
    const want = draftProvider.current;
    if (!want || !meta.providers.length || ((meta.logos || []).length && !logoCode)) return;
    draftProvider.current = '';
    if (providerOptions.some((p) => p.code === want)) setProvider(want);
  }, [meta.providers, logoCode, mode, provider]);

  /* แนบรูป — เลือกได้หลายไฟล์ · ย่อเหลือ 1024px ฝั่ง browser ก่อนส่ง (แพทเทิร์นเดียวกับ ProductUpload.jsx เดิม)
     เพดานจำนวนมาจาก backend (max_attach) — สินค้าในแคมเปญมีหลายตัว ผู้ใช้ขอ 23 ก.ย. */
  const maxAttach = Number(providerPicked && providerPicked.max_images) || meta.max_attach || 16;
  const sourceLimit = maxAttach - (hasLogo ? 1 : 0) - (hasMascot ? 1 : 0);

  const resizeOne = (file) =>
    new Promise((resolve) => {
      Resizer.imageFileResizer(file, 1024, 1024, 'JPEG', 92, 0, (uri) => resolve(uri), 'base64');
    });

  /* อ่านไฟล์จาก <input type=file> → รายการ {id, uri} หรือ null ถ้าไม่ผ่าน (ใช้ร่วมแผงหลักและหน้าต่างแก้ไข) */
  const readImageFiles = async (e, slots, limitText, setBusy) => {
    const requestedCampaign = id;
    const files = Array.from(e.target.files || []);
    e.target.value = ''; // เลือกไฟล์เดิมซ้ำได้

    if (!files.length) return null;

    if (slots <= 0) {
      showNotify(limitText, 'warning');
      return null;
    }
    if (files.length > slots) {
      showNotify(`เหลือที่ว่าง ${slots} รูป เลือกไฟล์ใหม่ — ยังไม่ได้เพิ่มรูป`, 'warning');
      return null;
    }

    if (files.some((file) => !/^image\/(jpeg|png|webp)$/.test(file.type))) {
      showNotify('เลือกรูป JPG, PNG หรือ WebP เท่านั้น', 'warning');
      return null;
    }
    setBusy(true);
    try {
      const uris = await Promise.all(files.map(resizeOne));
      if (!alive.current || activeCampaign.current !== requestedCampaign) return null;
      return uris.map((uri) => ({
        id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        uri,
      }));
    } catch (err) {
      showNotify('อ่านรูปไม่สำเร็จ ลองไฟล์อื่น', 'error');
      return null;
    } finally {
      if (alive.current) setBusy(false);
    }
  };

  const attachFile = async (e) => {
    const slots = sourceLimit - attachList.length - (mode === 'products' ? productSel.length : 0);
    const items = await readImageFiles(
      e,
      slots,
      `แนบรูปสินค้าได้สูงสุด ${sourceLimit} รูป (นับโลโก้และมาสคอตในเพดานแล้ว)`,
      setAttaching
    );
    if (items) setAttachList((prev) => [...prev, ...items]);
  };

  const removeAttach = (id) => setAttachList((prev) => prev.filter((a) => a.id !== id));

  /* หน้าต่างแก้ไข: เพดาน = ของโมเดลแก้ภาพ หัก 1 ให้ภาพเดิม */
  const editLimit = (Number(editPicked && editPicked.max_images) || meta.max_attach || 16) - 1;
  const editCount = editProductSel.length + editAttach.length;
  const attachEditFile = async (e) => {
    const items = await readImageFiles(
      e,
      editLimit - editCount,
      `แนบรูปเพิ่มได้สูงสุด ${editLimit} รูป (รวมภาพเดิมเป็น ${editLimit + 1})`,
      setEditAttaching
    );
    if (items) setEditAttach((prev) => [...prev, ...items]);
  };
  const removeEditAttach = (aid) => setEditAttach((prev) => prev.filter((a) => a.id !== aid));
  const toggleEditProduct = (pid) =>
    setEditProductSel((prev) =>
      prev.includes(pid) ? prev.filter((x) => x !== pid) : [...prev, pid]
    );

  /* งานที่ยังไม่จบ — poll ทุก 6 วินาที (ห้ามถี่กว่า 5 วิ เพราะ preventDuplicate)
     ปุ่มสร้าง/แจ้งเตือนดูเฉพาะงานของตัวเอง — เปิด "ดูของทุกคน" แล้วงานค้างของคนอื่นเคยล็อกปุ่มและเด้งแจ้งเตือน (รีเช็ค 27 ต.ค. 2569) */
  const isPendingRow = (g) => g.status === 'PENDING' || g.status === 'RUNNING';
  const isMine = (g) => String(g.created_by) === String(app.user.id);
  const anyPending = list.some(isPendingRow);
  const hasPending = list.some((g) => isPendingRow(g) && isMine(g));

  useEffect(() => {
    if (!anyPending) return undefined;

    const timer = setInterval(() => {
      /* รอบก่อนยังไม่กลับ = ข้าม — เดิมยิงซ้อนทุก 6 วิ ดาวน์โหลดซ้ำ/แจ้ง Telegram ซ้ำ (16 ต.ค. 2569) */
      if (polling.current) return;
      polling.current = true;
      const pending = list.filter(isPendingRow);

      Promise.all(
        pending.map((g) =>
          FetchData('OPTIONS', `/${mod}/poll`, app.token, { id: g.id, _t: Date.now() })
        )
      )
        .then((results) => {
          if (!alive.current) return;
          const mine = results.filter((r, i) => isMine(pending[i]));
          if (mine.some((r) => r.state === 'SUCCESS')) {
            showNotify('ภาพเสร็จแล้ว', 'success');
          } else if (mine.some((r) => r.state === 'FAILED')) {
            showNotify('สร้างภาพไม่สำเร็จ ดูสาเหตุในรายการผลงาน', 'error');
          }
          if (results.some((r) => r.state === 'SUCCESS' || r.state === 'FAILED')) {
            loadList();
          }
        })
        .catch(() => {
          // เครือข่ายสะดุด — รอบถัดไปลองใหม่เอง
        })
        .finally(() => {
          polling.current = false;
        });
    }, 6000);

    return () => clearInterval(timer);
  }, [anyPending, list]);

  /* ส่วน "ข้อมูลจริงของแคมเปญ" ที่ต่อท้ายทุกสไตล์ — ชื่อ ขั้นราคา สินค้า ช่วงเวลา (ผู้ใช้แก้ต่อได้) */
  const DEFAULT_STYLE =
    'โปสเตอร์โปรโมชันสำหรับโพสต์โซเชียลมีเดีย ตัวอักษรไทยคมชัด วรรณยุกต์ถูกตำแหน่ง ตัวเลขราคาอ่านง่าย สไตล์โฆษณาร้านค้าปลีก สดใส สะอาดตา';

  /* ค่าตั้งต้นของฟอร์มจากข้อมูลจริงของแคมเปญ — ขั้นราคา (ลูกค้าตั้งเป็น "ซื้อ N ชิ้น = ราคา") · สินค้าที่ร่วม · ช่วงเวลา
     ผู้ใช้เห็นเป็นช่องธรรมดา แก้ได้ ไม่ต้องรู้จัก prompt */
  const formFromCampaign = (c) => {
    if (!c) return { headline: '', sub: '', products: '', period: 'Y', extra: '' };
    /* ความหมาย quantity/price ต่างตามชนิดโปรโมชัน (campaigns_type · ตรงกับ promotion.class.php) — เดิมทุกชนิดได้ "ซื้อ N ชิ้น X บาท" (16 ต.ค. 2569)
       A/D ซื้อครบจำนวนลดราคา: quantity ชิ้น = price บาท · B ซื้อครบมูลค่าได้ของแถม: price = ยอดซื้อ
       C ซื้อครบจำนวนได้ของแถม: quantity ชิ้น · amount = จำนวนของแถม · E ซื้อครบมูลค่าได้ส่วนลด: quantity = ยอดซื้อ · price = ส่วนลด
       backend รุ่นเก่าไม่ส่ง promotion_type → ถือเป็น A แบบเดิม */
    const type = c.promotion_type || 'A';
    const qty = Number(c.quantity || 0);
    const price = Number(c.price || 0);
    const amount = Number(c.amount || 0);
    const first = (c.products || [])[0];
    const normal = first ? Number(first.normal_price || 0) : 0;
    let headline = '';
    let sub = '';
    if ((type === 'A' || type === 'D') && qty > 0 && price > 0) {
      headline = qty === 1 ? `ราคาพิเศษ ${price} บาท` : `ซื้อ ${qty} ชิ้น ${price} บาท`;
      if (normal > 0 && normal * qty > price) sub = `ปกติชิ้นละ ${normal} บาท`;
    } else if (type === 'E' && qty > 0 && price > 0) {
      headline = `ซื้อครบ ${Func.numberDisplay(qty)} บาท ลด ${Func.numberDisplay(price)} บาท`;
    } else if (type === 'B' && price > 0) {
      headline = `ซื้อครบ ${Func.numberDisplay(price)} บาท รับของแถม`;
    } else if (type === 'C' && qty > 0) {
      headline = `ซื้อครบ ${Func.numberDisplay(qty)} ชิ้น รับของแถม${
        amount > 0 ? ` ${Func.numberDisplay(amount)} ชิ้น` : ''
      }`;
    } else {
      headline = c.promotion_name || c.name || '';
    }
    if (!sub && c.promotion_name && c.promotion_name !== c.name && c.promotion_name !== headline) {
      sub = c.promotion_name;
    }
    const names = (c.products || [])
      .slice(0, 3)
      .map((x) => x.name)
      .filter(Boolean);
    let products = '';
    if (c.is_all_products === 'Y') {
      products = 'สินค้าทุกรายการในร้าน';
    } else if (names.length) {
      products =
        names.join(', ') +
        (c.product_count > names.length ? ` และอื่น ๆ รวม ${c.product_count} รายการ` : '');
    }
    return { headline, sub, products, period: 'Y', extra: '' };
  };

  /* ประกอบคำสั่งจากสไตล์ + ฟอร์ม — จุดเดียวที่สร้างข้อความส่ง AI (โหมดธรรมดา) */
  const composePrompt = (presetOverride, ratioOverride) => {
    const useRatio = ratioOverride || ratio;
    const preset = presetOverride || (meta.presets || []).find((p) => p.code === presetCode);
    let style = preset ? preset.prompt : DEFAULT_STYLE;
    if (mode === 'none') {
      style = style
        .replace(/สินค้าจริงจากรูปที่แนบ/g, 'สินค้าที่ให้ AI วาดขึ้นใหม่')
        .replace(/จาก(?:รูป|ภาพ)ที่แนบ/g, 'ที่ให้ AI วาดขึ้นใหม่');
    }
    const parts = [style];
    /* สัดส่วนภาพ = สื่อที่จะลง (6 ต.ค. 2569 เจ้าของงานทักว่าสไตล์ต้องไม่ใช่ขนาด) → เติมคำแนะนำจัดวางของสื่อนั้นให้เอง */
    if (RATIO_HINTS[useRatio]) parts.push(RATIO_HINTS[useRatio]);
    if (campaign && campaign.name) parts.push(`แคมเปญ "${campaign.name}"`);
    if (form.headline.trim()) parts.push(`ข้อความหลักตัวใหญ่ว่า "${form.headline.trim()}"`);
    if (form.sub.trim()) parts.push(`ข้อความรองว่า "${form.sub.trim()}"`);
    if (mode === 'products') {
      const selected = ((campaign && campaign.product_images) || []).filter((img) =>
        productSel.includes(img.product_id)
      );
      if (selected.length)
        parts.push(
          `สินค้าในภาพคือ ${selected
            .map((img) => String(img.name || img.sku).replace(/^\(NA\)\s*/i, ''))
            .join(', ')} ใช้สินค้าจริงจากรูปที่แนบเท่านั้น ไม่ต้องวาดสินค้าอื่น`
        );
      if (attachList.length)
        parts.push(`ใช้ภาพอัปโหลดเพิ่มเติม ${attachList.length} ภาพเป็นข้อมูลอ้างอิงร่วมด้วย`);
    } else if (mode === 'upload' && attachList.length) {
      parts.push('ใช้สินค้าและองค์ประกอบจากภาพอัปโหลดที่แนบ ไม่เพิ่มสินค้าอื่น');
    } else if (mode === 'current') {
      parts.push('ใช้ภาพหลักแคมเปญที่แนบเป็นต้นแบบ');
    } else if (mode === 'none') {
      parts.push('ไม่มีรูปสินค้าอ้างอิง ให้สร้างภาพขึ้นใหม่จากข้อความ');
    }
    if (form.period === 'Y' && campaign && campaign.date_start && campaign.date_stop) {
      parts.push(
        `ช่วง ${Func.dateDisplay(campaign.date_start)} ถึง ${Func.dateDisplay(campaign.date_stop)}`
      );
    }
    if (form.extra.trim()) parts.push(form.extra.trim());
    if (mascotOn && mascotCode) {
      parts.push(
        meta.mascot_prompt ||
          'ใช้ตัวมาสคอตช้างสีแดงชุดไทยจากรูปที่แนบ วางในภาพ 1 ตัว คงหน้าตาและสีเดิมทุกประการ ห้ามวาดมาสคอตขึ้นเอง'
      );
    }
    if (logoOn && logoCode) {
      parts.push(
        logoLayout
          ? layoutText(logoPos, logoXYc, logoSize, useRatio, logoAR)
          : meta.logo_prompt ||
              'ใส่โลโก้ร้านจากรูปที่แนบรูปสุดท้ายไว้มุมบนซ้ายขนาดพอเหมาะ ห้ามวาดหรือแต่งโลโก้ขึ้นเอง'
      );
    }
    return parts.join(' ');
  };

  const logoSelected = (meta.logos || []).find((l) => l.code === logoCode) || null;
  const mascotSelected = (meta.mascots || []).find((m) => m.code === mascotCode) || null;
  /* ตัดประโยคที่ระบบเติมให้ (โลโก้ให้ AI วาด · มาสคอต · เว้นที่วางโลโก้) — ตรงกับ stripSystemSentences ฝั่ง server
     รีเช็ค 26 ต.ค.: "ลองใหม่"/คำสั่งขั้นสูงเคยพกประโยคของรอบก่อน ทั้งที่รอบนี้ไม่ได้แนบโลโก้/มาสคอต */
  const stripSystem = (value) =>
    [meta.logo_prompt, meta.mascot_prompt]
      .filter(Boolean)
      .reduce((v, sentence) => v.split(sentence).join(''), String(value || ''))
      .replace(
        /เว้นพื้นที่ว่าง[^\n]*?สำหรับวางโลโก้ร้านภายหลัง[^\n]*?ห้ามวาดโลโก้หรือชื่อร้านขึ้นเอง/g,
        ''
      )
      .replace(
        /รูปที่แนบรูปสุดท้ายคือผังตำแหน่งโลโก้ร้าน[^\n]*?ห้ามวางข้อความ ราคา หรือสินค้าทับโลโก้/g,
        ''
      )
      .replace(/[ \t]{2,}/g, ' ')
      .trim();
  // รวมคำสั่งโลโก้/มาสคอตที่ backend จะเติมคืนแม้ร่างขั้นสูงลบทิ้ง ให้เห็นและนับก่อนส่ง
  const fullPrompt = () => {
    let value = stripSystem(advanced ? prompt : composePrompt());
    if (!value.trim()) return value;
    if (mascotOn && mascotCode)
      value = `${value} ${
        meta.mascot_prompt ||
        'ใช้ตัวมาสคอตช้างสีแดงชุดไทยจากรูปที่แนบ วางในภาพ 1 ตัว คงหน้าตาและสีเดิมทุกประการ ห้ามวาดมาสคอตขึ้นเอง'
      }`;
    /* server ต่อประโยคโลโก้ให้เสมอ (ผังตำแหน่ง หรือ LOGO_PROMPT) — โชว์และนับความยาวด้วย */
    if (logoOn && logoCode)
      return `${value} ${
        logoLayout
          ? layoutText(logoPos, logoXYc, logoSize, ratio, logoAR)
          : meta.logo_prompt ||
            'ใส่โลโก้ร้านจากรูปที่แนบรูปสุดท้ายไว้มุมบนซ้ายขนาดพอเหมาะ ห้ามวาดหรือแต่งโลโก้ขึ้นเอง'
      }`;
    return value;
  };
  /* ไม่รวมประโยคระบบ (ผังโลโก้/มาสคอต) — server ต่อให้เองทุกครั้ง · เดิมย้าย/ย่อโลโก้ = คำสั่งที่แก้เอง "ไม่ตรงตัวเลือก" ปุ่มสร้างล็อก (รีเช็ค 27 ต.ค. 2569) */
  const promptKey = () =>
    JSON.stringify([
      stripSystem(composePrompt()),
      mode,
      ratio,
      logoOn,
      logoCode,
      mascotOn,
      mascotCode,
      productSel,
      attachList.map((a) => a.id),
    ]);

  /* ปุ่ม "ดึงข้อความจากข้อมูลแคมเปญใหม่" — ล้างที่แก้ไว้แล้วเติมค่าตั้งต้นจากแคมเปญ */
  const resetForm = () => {
    if (!campaign) return;
    setForm(formFromCampaign(campaign));
    setResetConfirm(false);
  };

  const requestResetForm = () => {
    if (!campaign) return;
    if (JSON.stringify(form) !== JSON.stringify(formFromCampaign(campaign))) setResetConfirm(true);
    else resetForm();
  };

  /* สไตล์ไม่เปลี่ยนสัดส่วนหรือทับคำสั่งที่แก้เอง; promptStale เตือนเมื่อร่างไม่ตรงตัวเลือก */
  const applyPreset = (preset) => {
    setPresetCode(preset.code);
    /* สไตล์มาสคอต = แนบมาสคอตตัวจริงให้ทันที (ไม่แนบ AI จะวาดช้างเอง) — ปิดเองได้ */
    if (preset.code === 'cartoon_mascot' && (meta.mascots || []).length) setMascotOn(true);
    setStyleOpen(false);
  };

  /* ตัวเลือกเปลี่ยนหลังแก้คำสั่งเอง → ยืนยันใช้คำสั่งที่พิมพ์ไว้ต่อกับตัวเลือกใหม่ (เดิมมีแต่ทางที่ทิ้งคำสั่ง · 18 ต.ค. 2569) */
  const keepPrompt = () => setPromptSnapshot(promptKey());

  /* สลับโหมดขั้นสูง — เปิดแล้วเริ่มจากคำสั่งที่ฟอร์มประกอบไว้ จะได้ไม่ต้องพิมพ์เองตั้งแต่ศูนย์ */
  const toggleAdvanced = (on) => {
    if (on) {
      setPrompt(stripSystem(composePrompt()));
      setPromptSnapshot(promptKey());
    }
    setAdvanced(on);
  };

  /* โหลดข้อมูลแคมเปญเสร็จ → เติมฟอร์มให้ก่อนเลย ผู้ใช้แค่ตรวจแล้วกดสร้าง */
  useEffect(() => {
    if (!campaign || String(campaign.id) !== String(id) || initializedCampaign.current === id)
      return;
    initializedCampaign.current = id;
    const base = formFromCampaign(campaign);
    setForm(base);
    /* เลือกให้ไม่เกินเพดานตั้งแต่เปิดหน้า — แคมเปญมีรูปสินค้า 16 ตัว + โลโก้ (ค่าตั้งต้น) = 17 จะปิดปุ่มสร้างทันที
       (สำเนาฐานลูกค้ามี 125 แคมเปญที่สินค้ามีรูป ≥ 16 ตัว · ตรวจ 6 ต.ค. 2569) */
    const firstPick = Math.max(1, maxAttach - (logoOn ? 1 : 0) - (mascotOn ? 1 : 0));
    setProductSel((campaign.product_images || []).slice(0, firstPick).map((img) => img.product_id));
    setMode((campaign.product_images || []).length ? 'products' : 'none');

    /* กู้ร่างทับค่าตั้งต้นจากแคมเปญ · ร่างเสีย/หมดอายุ = ข้าม */
    draftLoaded.current = draftKey;
    draftCleared.current = '';
    let draft = null;
    try {
      draft = JSON.parse(sessionStorage.getItem(draftKey));
    } catch (_) {
      draft = null;
    }
    const d = draft && Date.now() - Number(draft.at) < DRAFT_TTL ? draft.data : null;
    if (!d || typeof d !== 'object') return;
    const str = (v, fallback) => (typeof v === 'string' ? v : fallback);
    setForm({
      ...base,
      headline: str(d.headline, base.headline),
      sub: str(d.sub, base.sub),
      extra: str(d.extra, base.extra),
      period: d.period === 'N' ? 'N' : 'Y',
    });
    setPresetCode(str(d.presetCode, ''));
    if (str(d.ratio, '')) setRatio(d.ratio);
    if (str(d.resolution, '')) setResolution(d.resolution);
    if (str(d.provider, 'auto') !== 'auto') {
      draftProvider.current = d.provider;
      setProvider(d.provider);
    }
    if (typeof d.logoOn === 'boolean') setLogoOn(d.logoOn);
    if (str(d.logoCode, '') && (meta.logos || []).some((l) => l.code === d.logoCode))
      setLogoCode(d.logoCode);
    if (typeof d.mascotOn === 'boolean') setMascotOn(d.mascotOn);
    if (str(d.mascotCode, '') && (meta.mascots || []).some((m) => m.code === d.mascotCode))
      setMascotCode(d.mascotCode);
    if (str(d.logoPos, '')) setLogoPos(d.logoPos);
    if (Number(d.logoSize) > 0) setLogoSize(Number(d.logoSize));
    if (
      Array.isArray(d.logoXY) &&
      d.logoXY.length === 2 &&
      d.logoXY.every((v) => Number.isFinite(Number(v)))
    )
      setLogoXY(d.logoXY.map(Number));
    if (d.advanced === true && typeof d.prompt === 'string') {
      setAdvanced(true);
      setPrompt(d.prompt);
      setPromptSnapshot(str(d.promptSnapshot, ''));
    }
  }, [campaign, id]);

  /* ค่าที่เก็บเป็นร่าง — ไม่มีรูปอัปโหลด/รูปสินค้าที่ติ๊ก (16 ต.ค. 2569) */
  const draftData = () => ({
    headline: form.headline,
    sub: form.sub,
    extra: form.extra,
    period: form.period,
    presetCode,
    ratio,
    resolution,
    provider: draftProvider.current || provider,
    advanced,
    prompt: advanced ? prompt : '',
    promptSnapshot: advanced ? promptSnapshot : '',
    logoPos,
    logoSize: Number(logoSize),
    logoXY,
    logoOn,
    logoCode,
    mascotOn,
    mascotCode,
  });

  /* เก็บร่างทุกครั้งที่แก้ (หลังกู้แล้วเท่านั้น) · เหมือนค่าตั้งต้นของแคมเปญ = ลบร่าง */
  useEffect(() => {
    if (draftLoaded.current !== draftKey || !campaign) return;
    const data = draftData();
    const sig = JSON.stringify(data);
    if (sig === draftCleared.current) return;
    draftCleared.current = '';
    const base = formFromCampaign(campaign);
    const dirty =
      ['headline', 'sub', 'extra', 'period'].some((k) => data[k] !== base[k]) ||
      presetCode !== '' ||
      ratio !== '1:1' ||
      resolution !== '1K' ||
      data.provider !== 'auto' ||
      advanced ||
      logoPos !== 'tl' ||
      Number(logoSize) !== 18 ||
      !logoOn ||
      mascotOn;
    try {
      if (dirty) sessionStorage.setItem(draftKey, JSON.stringify({ at: Date.now(), data }));
      else sessionStorage.removeItem(draftKey);
    } catch (_) {
      // เบราว์เซอร์ปิด storage — ใช้งานต่อได้ แค่ไม่มีร่าง
    }
  }, [
    form,
    presetCode,
    ratio,
    resolution,
    provider,
    advanced,
    prompt,
    logoPos,
    logoSize,
    logoXY,
    logoOn,
    logoCode,
    mascotOn,
    mascotCode,
  ]);

  /* ส่งงานสำเร็จแล้วรูปอัปโหลดถูกล้าง → promptKey เปลี่ยน คำสั่งที่แก้เองถูกล็อกว่าไม่ตรงตัวเลือก
     จับ snapshot ใหม่หลังล้าง ให้กดสร้างซ้ำได้โดยไม่ต้องพิมพ์ใหม่ (16 ต.ค. 2569) */
  useEffect(() => {
    if (!resnapAfterSubmit.current) return;
    resnapAfterSubmit.current = false;
    setPromptSnapshot(promptKey());
  }, [attachList]);

  /* "ลองใหม่" เปลี่ยนสัดส่วน/คำสั่งพร้อมกัน — จับ snapshot หลัง state ใหม่เข้าที่ ไม่งั้นปุ่มสร้างถูกล็อกทันที (18 ต.ค. 2569) */
  useEffect(() => {
    if (retryTick) setPromptSnapshot(promptKey());
  }, [retryTick]);

  /* หมวดของสไตล์ เรียงตามที่พบครั้งแรก (backend เรียง sort มาแล้ว) · การ์ดที่โชว์ = หมวดที่เลือก */
  const presetList = meta.presets || [];
  const presetCategories = presetList.reduce((acc, p) => {
    const c = p.category || 'อื่น ๆ';
    if (!acc.includes(c)) acc.push(c);
    return acc;
  }, []);
  /* "ยอดนิยม" จากการใช้จริง (meta.preset_usage · 0019): ใช้บ่อยก่อน แล้วใช้ล่าสุดก่อน · ยังไม่เคยใช้เลย = ลำดับจากตาราง */
  const POPULAR_COUNT = 6;
  const usageOf = (code) => (meta.preset_usage && meta.preset_usage[code]) || null;
  const popularList = [...presetList].sort((a, b) => {
    const ua = usageOf(a.code);
    const ub = usageOf(b.code);
    const ca = ua ? ua.count : 0;
    const cb = ub ? ub.count : 0;
    if (cb !== ca) return cb - ca;
    const la = ua ? ua.last : '';
    const lb = ub ? ub.last : '';
    if (la === lb) return 0;
    return la < lb ? 1 : -1;
  });
  let presetVisible = presetList;
  if (presetCat) {
    presetVisible = presetList.filter((p) => (p.category || 'อื่น ๆ') === presetCat);
  } else if (!showAll) {
    presetVisible = popularList.slice(0, POPULAR_COUNT);
  }

  /* รูปหลักของสินค้าในแคมเปญ (backend กรองเฉพาะที่มีไฟล์จริง) — โหมดตั้งต้นถ้ามี */
  const productImages = (campaign && campaign.product_images) || [];
  const productPicked = productImages.filter((img) => productSel.includes(img.product_id));
  const toggleProduct = (pid) =>
    setProductSel((prev) => (prev.includes(pid) ? prev.filter((x) => x !== pid) : [...prev, pid]));
  const selectedPreset = presetList.find((p) => p.code === presetCode) || null;

  /* เครดิตโดยประมาณของโมเดลที่จะใช้ (auto = โมเดลตั้งต้นของโหมดนั้น) — ตัวเลขจริงมาจาก creditsConsumed ตอนเสร็จ */
  const providerInUse = providerPicked;
  const estCredits = providerInUse ? creditOf(providerInUse, resolution) : 0;
  const sourceCount =
    mode === 'products'
      ? productPicked.length + attachList.length
      : mode === 'upload'
      ? attachList.length
      : mode === 'none'
      ? 0
      : 1;
  const referenceCount = sourceCount + (hasLogo ? 1 : 0) + (hasMascot ? 1 : 0);
  const tooManyRefs = referenceCount > maxAttach;
  const promptStale = advanced && promptSnapshot !== promptKey();
  const noSource = (mode === 'products' || mode === 'upload') && sourceCount === 0;
  const mixedUnsupported =
    mode === 'products' && attachList.length > 0 && meta.supports_mixed_refs !== 'Y';

  /* การ์ดที่ยังไม่มีรูปตัวอย่าง — วาดจากสีประจำสไตล์ (swatch) แทน */
  const swatchBg = (sw) => {
    const colors = Array.isArray(sw) && sw.length ? sw : ['#B0BEC5', '#ECEFF1'];
    const stops = colors.length === 1 ? [colors[0], colors[0]] : colors;
    return `linear-gradient(135deg, ${stops.join(', ')})`;
  };

  /* รหัสผิดพลาดจาก backend → ข้อความบนหน้าจอ ใช้ร่วมทุกปุ่ม · ไม่รู้จัก = undefined ให้ผู้เรียกใช้ข้อความกลาง
     (16 ต.ค. 2569 — เดิมโควตายังไม่ตั้ง/ไฟล์ใหญ่/อัปโหลดล้ม ขึ้นแค่ "บันทึกไม่สำเร็จ") */
  const errorText = (msg) =>
    ({
      ai_quota_not_set:
        'AI ปิดทั้งร้านอยู่ (โควตาเป็น 0) — ผู้ดูแลระบบตั้งโควตาได้ที่เมนู AI · ค่าใช้จ่ายและโควตา',
      ai_quota_exceeded: 'เกินโควตาประจำเดือนแล้ว',
      ai_user_quota_exceeded:
        'เครดิต AI เดือนนี้ของคุณครบตามที่ผู้ดูแลตั้งไว้แล้ว — ติดต่อผู้ดูแลระบบ',
      ai_kill_switch_on:
        'AI ปิดทั้งร้านอยู่ (หยุดฉุกเฉิน) — เปิดกลับได้ที่เมนู AI · ค่าใช้จ่ายและโควตา',
      ai_source_file_too_large: 'รูปอ้างอิงไฟล์ใหญ่เกินไป (ไม่เกิน 4MB)',
      ai_upload_failed: 'ส่งรูปให้ AI ไม่สำเร็จ ลองใหม่อีกครั้ง',
      ai_api_key_missing: 'ร้านยังไม่ได้เชื่อมต่อผู้ให้บริการ AI — แจ้งไอที',
      ai_attach_too_many: `รูปเกิน ${maxAttach} รูป (นับโลโก้และมาสคอตด้วย) — เอารูปออกก่อน`,
      permission_denied: 'บัญชีนี้ไม่มีสิทธิ์สั่งสร้างภาพ',
      ai_prompt_too_long: 'คำสั่งยาวเกิน 2,000 ตัวอักษร ลดข้อความให้สั้นลง',
      ai_no_current_image: 'แคมเปญนี้ยังไม่มีรูปหลักให้ใช้เป็นต้นแบบ',
      ai_mascot_not_found: 'ไม่พบไฟล์มาสคอต ปิดสวิตช์ใส่มาสคอตแล้วลองใหม่ — แจ้งไอที',
      ai_logo_pos_invalid: 'ตำแหน่ง/ขนาดโลโก้ไม่ถูกต้อง เลือกใหม่',
      not_owner: 'ภาพนี้เป็นของคนอื่น — จัดการได้เฉพาะเจ้าของภาพหรือผู้ดูแลระบบ AI',
      ai_logo_layout_failed: 'เตรียมผังตำแหน่งโลโก้ไม่สำเร็จ ลองใหม่',
      ai_logo_overlay_unavailable:
        'เซิร์ฟเวอร์ยังไม่รองรับวางโลโก้ (ยังไม่รันสคริปต์ฐานข้อมูล 0028) — แจ้งไอที',
      ai_logo_overlay_failed: 'วางโลโก้ไม่สำเร็จ ลองใหม่อีกครั้ง',
      ai_logo_overlay_base_missing: 'ไม่พบไฟล์ภาพต้นฉบับบนเซิร์ฟเวอร์ — แจ้งไอที',
      ai_logo_not_found: 'ไม่พบไฟล์โลโก้ร้าน ปิดช่องแนบโลโก้แล้วลองใหม่',
      ai_no_product_images: 'สินค้าในแคมเปญยังไม่มีรูปหลัก เลือกแนบรูปเองแทน',
      ai_provider_not_found: 'โมเดลนี้ใช้ไม่ได้แล้ว เลือกโมเดลอื่น',
      ai_source_gen_not_ready: 'ภาพที่จะแก้ยังไม่พร้อม รอสักครู่แล้วลองใหม่',
      ai_create_failed: 'ผู้ให้บริการ AI ไม่รับงาน ลองใหม่อีกครั้ง',
      ai_provider_not_loaded: 'โมเดลนี้ใช้ไม่ได้ตอนนี้ เลือกโมเดลอื่น',
      ai_prompt_required: 'ใส่คำสั่งสร้างภาพก่อน',
      ai_image_input_required: 'โมเดลนี้ต้องมีรูปอ้างอิงอย่างน้อย 1 รูป',
      ai_source_data_invalid: 'รูปที่แนบใช้ไม่ได้ ลองเลือกรูปใหม่',
      ai_source_invalid: 'รูปที่แนบใช้ไม่ได้ ลองเลือกรูปใหม่',
      ai_source_file_not_found: 'ไม่พบไฟล์รูปอ้างอิง โหลดหน้าใหม่แล้วลองอีกครั้ง',
      ai_download_failed: 'โหลดภาพผลลัพธ์ไม่สำเร็จ',
      ai_result_not_ready: 'ภาพยังไม่พร้อม รอสักครู่แล้วลองใหม่',
      ai_copy_failed: 'คัดลอกภาพเข้ารูปแคมเปญไม่สำเร็จ ลองใหม่อีกครั้ง',
      ai_generation_not_found: 'ไม่พบงานนี้แล้ว อาจถูกลบไปแล้ว',
      campaign_not_found: 'ไม่พบแคมเปญนี้',
      campaign_id_required: 'ไม่พบแคมเปญนี้',
    }[msg]);

  /* สาเหตุของงานที่ไม่สำเร็จ (error_msg) — เป็นรหัสของเรา หรือข้อความดิบจากผู้ให้บริการ/worker
     แปลเฉพาะที่รู้จัก ที่เหลือใช้ข้อความกลาง ไม่โชว์ข้อความดิบ (18 ต.ค. 2569) */
  const failReason = (msg) => {
    const raw = String(msg || '');
    if (errorText(raw)) return errorText(raw);
    if (/^timeout/i.test(raw)) return 'AI ใช้เวลานานเกินไป';
    if (/credit|balance|insufficient/i.test(raw)) return 'เครดิต AI ของร้านไม่พอ — แจ้งผู้ดูแลระบบ';
    if (/sensitive|moderat|flagged|policy|nsfw|safety/i.test(raw))
      return 'AI ไม่รับเนื้อหานี้ ลองปรับคำสั่งหรือรูป';
    return 'สร้างภาพไม่สำเร็จ';
  };

  /* "ลองใหม่" ของงานที่ไม่สำเร็จ — เติมคำสั่ง/สัดส่วน/ความละเอียด/โมเดลของงานนั้นกลับเข้าแผง ให้ตรวจแล้วกดสร้างเอง
     ไม่ส่งให้อัตโนมัติ · รูปอ้างอิงใช้ตามที่เลือกในแผงตอนนี้ (งานเก่าไม่ได้เก็บรูปที่แนบ) (18 ต.ค. 2569) */
  const retryFrom = (row) => {
    if (meta.ratios.includes(row.aspect_ratio)) setRatio(row.aspect_ratio);
    if (meta.resolutions.includes(row.resolution)) setResolution(row.resolution);
    if (providerOptions.some((p) => p.code === row.provider_code)) setProvider(row.provider_code);
    setPrompt(stripSystem(row.prompt || ''));
    setAdvanced(true);
    setRetryTick((n) => n + 1);
    showNotify('ใส่คำสั่งเดิมในแผงแล้ว กดสร้างภาพเพื่อลองใหม่', 'info');
  };

  const generate = () => {
    if (mode === 'products' && attachList.length > 0 && meta.supports_mixed_refs !== 'Y') {
      showNotify('แหล่งรูปแบบนี้ยังใช้ไม่ได้ เลือกแหล่งรูปอื่น', 'warning');
      return;
    }
    if (
      loading ||
      attaching ||
      hasPending ||
      meta.can_generate !== 'Y' ||
      (meta.usage && meta.usage.kill_switch === 'Y') ||
      promptStale
    )
      return;
    if (tooManyRefs) {
      showNotify(`รูปเกิน ${maxAttach} รูป (นับโลโก้และมาสคอตด้วย) — เอารูปออกก่อน`, 'warning');
      return;
    }
    const finalPrompt = fullPrompt();
    if (promptLength(finalPrompt) > 2000) {
      showNotify('คำสั่งยาวเกิน 2,000 ตัวอักษร ลดข้อความให้สั้นลง', 'warning');
      return;
    }
    if (!advanced && !presetCode && !form.headline.trim()) {
      showNotify('เลือกสไตล์ หรือกรอกข้อความหลักบนภาพก่อน', 'warning');
      return;
    }
    if (!finalPrompt.trim()) {
      showNotify('ใส่คำสั่งสร้างภาพก่อน', 'warning');
      return;
    }
    if (mode === 'upload' && attachList.length === 0) {
      showNotify('ใส่รูปอ้างอิงก่อน', 'warning');
      return;
    }
    if (mode === 'products' && sourceCount === 0) {
      showNotify(
        productImages.length
          ? 'ติ๊กเลือกรูปสินค้าอย่างน้อย 1 รูป'
          : 'สินค้าในแคมเปญยังไม่มีรูปหลัก เลือกแนบรูปเองแทน',
        'warning'
      );
      return;
    }

    setLoading(true);

    FetchData('PUT', `/${mod}`, app.token, {
      campaign_id: id,
      preset_code: presetCode,
      product_ids: mode === 'products' ? productPicked.map((img) => img.product_id) : [],
      logo: logoOn && logoCode ? logoCode : '',
      logo_pos: logoOn && logoCode && logoLayout ? logoPos : '',
      logo_size: logoSize,
      logo_x: logoPos === 'custom' ? Number(logoXYc[0].toFixed(2)) : 0,
      logo_y: logoPos === 'custom' ? Number(logoXYc[1].toFixed(2)) : 0,
      mascot: mascotOn && mascotCode ? mascotCode : '',
      prompt: finalPrompt,
      resolution: resolution,
      aspect_ratio: ratio,
      source: mode === 'products' && attachList.length ? 'refs' : mode,
      provider_code: provider === 'auto' ? '' : provider,
      filedatas: mode === 'upload' || mode === 'products' ? attachList.map((a) => a.uri) : [],
      _t: Date.now(),
    })
      .then((result) => {
        if (!alive.current) return;
        /* สำเร็จ = คงม่านโหลดจน loadList() ปลดเอง — ช่วงก่อนรายการใหม่มาปุ่มเคยเปิดให้กดซ้ำได้ (รีเช็ค 27 ต.ค. 2569) */
        if (result.status !== 'ok') setLoading(false);

        if (result.status === 'ok') {
          showNotify('ส่งงานแล้ว ภาพจะขึ้นในผลงานภายใน 2–5 นาที', 'success');
          /* ส่งแล้วลบร่าง — ยังไม่แก้อะไรเพิ่มก็ไม่เก็บใหม่ (16 ต.ค. 2569) */
          draftCleared.current = JSON.stringify(draftData());
          try {
            sessionStorage.removeItem(draftKey);
          } catch (_) {
            // ไม่มี storage ก็ไม่มีร่างให้ลบ
          }
          /* โหมดขั้นสูงเก็บคำสั่งที่แก้ไว้ให้ลองซ้ำ/แก้ต่อ (5 ต.ค. เดโมแล้วช่องว่างหลังส่ง ต้องประกอบใหม่) */
          if (!advanced) setPrompt('');
          else resnapAfterSubmit.current = true;
          setAttachList([]);
          loadList();
        } else {
          showNotify(errorText(result.msg) || t(`alert.saveerror`), 'error');
        }
      })
      .catch(() => {
        if (!alive.current) return;
        setLoading(false);
        showNotify('ส่งงานสร้างภาพไม่สำเร็จ ลองใหม่อีกครั้ง', 'error');
      });
  };

  /* "แก้ไข" ตามมติประชุม — สร้างรอบใหม่จากภาพผลลัพธ์เดิมด้วย prompt ใหม่ */
  const saveEdit = () => {
    if (promptLength(editPrompt) > 2000) {
      showNotify('คำสั่งแก้ภาพเกิน 2,000 ตัวอักษร ลดข้อความก่อนสร้าง', 'warning');
      return;
    }
    if (
      !editRow ||
      !editPrompt.trim() ||
      loading ||
      !editPicked ||
      meta.can_generate !== 'Y' ||
      meta.usage?.kill_switch === 'Y'
    )
      return;
    /* เปลี่ยนโมเดลหลังแนบรูปแล้ว เพดานอาจลดลง (GPT 15 → nano 13) — รีเช็ค 26 ต.ค.: เดิมกดได้แล้ว server ปัด */
    if (editCount > editLimit) {
      showNotify(
        `โมเดลนี้แนบรูปเพิ่มได้ ${editLimit} รูป — เอารูปออก ${editCount - editLimit} รูปก่อน`,
        'warning'
      );
      return;
    }

    setLoading(true);

    const refs =
      meta.supports_edit_refs === 'Y'
        ? { product_ids: editProductSel, filedatas: editAttach.map((a) => a.uri) }
        : {};
    FetchData('PATCH', `/${mod}/${editRow.id}`, app.token, {
      prompt: editPrompt,
      resolution: editResolution,
      aspect_ratio: editRatio,
      provider_code: editPicked.code,
      ...refs,
      _t: Date.now(),
    })
      .then((result) => {
        if (!alive.current) return;
        setLoading(false);
        if (result.status === 'ok') {
          setEditRow(null);
          showNotify('ส่งงานแก้ภาพแล้ว ภาพจะขึ้นในผลงานภายใน 2–5 นาที', 'success');
          loadList();
        } else if (result.msg === 'ai_attach_too_many') {
          showNotify(`แนบรูปเพิ่มได้ ${editLimit} รูปสำหรับโมเดลนี้ — เอารูปออกก่อน`, 'error');
        } else {
          showNotify(errorText(result.msg) || t(`alert.saveerror`), 'error');
        }
      })
      .catch(() => {
        if (alive.current) {
          setLoading(false);
          showNotify('ส่งงานแก้ภาพไม่สำเร็จ ลองใหม่อีกครั้ง', 'error');
        }
      });
  };

  /* โลโก้ที่วางไว้บนแถวแต่ไม่อยู่ในรายการของผู้ใช้คนนี้ (คนอื่น/สาขาอื่นวาง) — ใส่เป็นตัวเลือก "โลโก้ที่ใช้อยู่"
     รีเช็ค 26 ต.ค.: เดิมหาไม่เจอ → ปุ่ม "เอาโลโก้ออก" หาย และกดบันทึกแล้วเปลี่ยนเป็นโลโก้ของตัวเองเงียบ ๆ */
  const placerLogoCode = (row) =>
    ((meta.logos || []).find((l) => l.image === row.logo_url) || {}).code ||
    (row.logo_url ? CURRENT_LOGO : '');
  const placerLogos = (row) =>
    placerLogoCode(row) === CURRENT_LOGO
      ? [
          { code: CURRENT_LOGO, name: 'โลโก้ที่ใช้อยู่', image: row.logo_url },
          ...(meta.logos || []),
        ]
      : meta.logos || [];

  /* วาง/ย้าย/เอาออก โลโก้ (A3) — v = { logo, pos, x, y, size } · null = เอาออก · ไม่เสียเครดิต */
  const placeLogo = (row, v) => {
    if (!row || placing) return;
    setPlacing(true);
    const body = v
      ? { id: row.id, pos: v.pos, x: v.x, y: v.y, size: v.size, _t: Date.now() }
      : { id: row.id, remove: 'Y', _t: Date.now() };
    /* โลโก้ที่ใช้อยู่ (ของคนอื่น/สาขาอื่น) = ไม่ส่ง logo ให้ server ใช้โลโก้เดิมของแถว */
    if (v && v.logo && v.logo !== CURRENT_LOGO) body.logo = v.logo;
    FetchData('OPTIONS', `/${mod}/place_logo`, app.token, body)
      .then((result) => {
        if (!alive.current) return;
        setPlacing(false);
        if (result.status === 'ok') {
          setPlaceRow(null);
          showNotify(v ? 'วางโลโก้แล้ว' : 'เอาโลโก้ออกแล้ว', 'success');
          loadList();
        } else {
          showNotify(errorText(result.msg) || t(`alert.saveerror`), 'error');
        }
      })
      .catch(() => {
        if (alive.current) {
          setPlacing(false);
          showNotify('วางโลโก้ไม่สำเร็จ ลองใหม่อีกครั้ง', 'error');
        }
      });
  };

  const approve = (row) => {
    setLoading(true);

    FetchData('OPTIONS', `/${mod}/approve`, app.token, { id: row.id, _t: Date.now() })
      .then((result) => {
        if (!alive.current) return;
        setLoading(false);

        if (result.status === 'ok') {
          showNotify('เก็บเข้ารูปแคมเปญแล้ว', 'success');
          loadList();
          if (onSaved) onSaved();
        } else if (result.msg === 'ai_already_approved') {
          showNotify('ภาพนี้อยู่ในรูปแคมเปญแล้ว', 'warning');
        } else {
          showNotify(errorText(result.msg) || t(`alert.saveerror`), 'error');
        }
      })
      .catch(() => {
        if (!alive.current) return;
        setLoading(false);
        showNotify(t(`alert.saveerror`), 'error');
      });
  };

  const remove = () => {
    if (!delRow) return;

    setLoading(true);

    FetchData('DELETE', `/${mod}/${delRow.id}`, app.token, {})
      .then((result) => {
        if (!alive.current) return;
        setLoading(false);
        setDelRow(null);

        if (result.status === 'ok') {
          showNotify(t(`alert.deletecomplete`), 'success');
          loadList();
        } else {
          showNotify(errorText(result.msg) || t(`alert.deleteerror`), 'error');
        }
      })
      .catch(() => {
        if (!alive.current) return;
        setLoading(false);
        showNotify(t(`alert.deleteerror`), 'error');
      });
  };

  const { usage } = meta;
  /* ด่านค่าใช้จ่ายเดียวกับ guardCost() ฝั่ง server — ปิดปุ่มสร้างตั้งแต่เปิดหน้า ไม่ต้องกรอกเสร็จแล้วค่อยรู้ (18 ต.ค. 2569) */
  const costBlock = (() => {
    if (!usage) return '';
    if (usage.kill_switch === 'Y') return 'ai_kill_switch_on';
    if (Number(usage.quota) <= 0) return 'ai_quota_not_set';
    if (
      (usage.over_quota || 'STOP') === 'STOP' &&
      Number(usage.month_images) >= Number(usage.quota)
    )
      return 'ai_quota_exceeded';
    return '';
  })();
  const canEdit = meta.can_generate === 'Y';
  const canGenerate = canEdit && !costBlock;

  /* "สร้างใหม่พร้อมโลโก้" (27 ต.ค. 2569 · เจ้าของงาน: ไม่เอาแก้ภาพเดิมแล้วเติมโลโก้ "มันแปะมา" — ให้เจนใหม่ทั้งภาพโดยมีโลโก้ตั้งแต่แรก)
     สร้างงานใหม่ (PUT) ด้วยคำสั่งเดิมของงานนั้น (ตัดประโยคระบบออก) + ผังตำแหน่งโลโก้ · สัดส่วน/ความละเอียดเดิม · ภาพเดิมยังอยู่ ได้การ์ดใหม่
     รูปอ้างอิง (รีเช็ค 27 ต.ค.): งานเก่าไม่ได้เก็บรูปที่แนบ → อ่านจากคำสั่งเดิมว่าใช้รูปแบบไหน แล้วส่งรูปชุดเดียวกัน
       สินค้าในแคมเปญ = จับชื่อสินค้าในคำสั่งเดิมกลับเป็นรูปสินค้า · ไม่ใช้รูป = ไม่ส่งรูป · ภาพหลักแคมเปญ = ส่งภาพหลัก
       รูปที่อัปโหลดเอง = ต้องแนบใหม่ที่แผงซ้าย (ระบบไม่ได้เก็บ) · งานจากปุ่ม "แก้ไข"/คำสั่งที่แก้เอง = ใช้รูปและคำสั่งจากแผงตอนนี้ */
  const regenProvider = (row) =>
    (meta.providers || []).find((p) => p.code === row.provider_code && p.kind === 'IMAGE_EDIT') ||
    (meta.providers || []).find((p) => p.code === meta.default_edit);
  const regenPlan = (row) => {
    if (!row) return { error: 'งานนี้ไม่มีคำสั่งเดิม ใช้แผงด้านซ้ายสร้างใหม่แทน' };
    const own = stripSystem(row.prompt || '');
    const isDesign =
      Boolean(campaign && campaign.name) && own.includes(`แคมเปญ "${campaign.name}"`);
    const rowRatio = row.aspect_ratio || '1:1';
    /* รูปแบบรูปของงานเดิม — ประโยคเดียวกับที่ composePrompt เขียนไว้ */
    let jobMode = '';
    if (isDesign) {
      if (own.includes('ไม่มีรูปสินค้าอ้างอิง')) jobMode = 'none';
      else if (own.includes('ใช้ภาพหลักแคมเปญที่แนบเป็นต้นแบบ')) jobMode = 'current';
      else if (own.includes('ใช้สินค้าจริงจากรูปที่แนบเท่านั้น')) jobMode = 'products';
      else if (own.includes('ใช้สินค้าและองค์ประกอบจากภาพอัปโหลดที่แนบ')) jobMode = 'upload';
    }
    const jobUploads = isDesign && /ใช้ภาพอัปโหลดเพิ่มเติม \d+ ภาพ/.test(own);
    const plan = {
      prompt: isDesign
        ? own
        : [stripSystem(composePrompt(undefined, rowRatio)), own].filter(Boolean).join(' '),
      /* มาสคอต: งานออกแบบดูจากประโยคมาสคอตที่ server ต่อไว้ · งานแก้ไขใช้สวิตช์ในแผง
         หรือคำสั่งแก้ที่สั่งคงมาสคอตไว้ (เช่น "คง…มาสคอตเดิม") — แต่ "เอามาสคอตออก/ไม่ใส่มาสคอต" ไม่นับ */
      mascot: isDesign
        ? Boolean(meta.mascot_prompt) && String(row.prompt || '').includes(meta.mascot_prompt)
        : (mascotOn && Boolean(mascotCode)) ||
          (/มาสคอต/.test(own) &&
            !/(เอา|ลบ)มาสคอต\S*ออก|ไม่(ต้อง)?(ใส่|มี|เอา)มาสคอต|ห้าม\S*มาสคอต/.test(own)),
      mode: jobMode || mode,
      productIds: [],
      files: [],
      fromJob: Boolean(jobMode),
    };
    if (!plan.prompt) return { error: 'งานนี้ไม่มีคำสั่งเดิม ใช้แผงด้านซ้ายสร้างใหม่แทน' };
    if (plan.mode === 'products') {
      if (jobMode) {
        const seg = own.split('สินค้าในภาพคือ ')[1] || '';
        const names = seg.split(' ใช้สินค้าจริงจากรูปที่แนบเท่านั้น')[0].split(', ');
        /* ตรงตัวทั้งชื่อ — ชื่อสั้นเป็นส่วนหนึ่งของชื่อยาว (X กับ X #รุ่นย่อย) เคยติดมาทั้งคู่ (รีเช็ค 27 ต.ค. 2569) */
        plan.productIds = productImages
          .filter((img) => {
            const nm = String(img.name || img.sku).replace(/^\(NA\)\s*/i, '');
            return nm && names.includes(nm);
          })
          .map((img) => img.product_id);
      } else {
        plan.productIds = productPicked.map((img) => img.product_id);
      }
      if (!jobMode || jobUploads) plan.files = attachList.map((a) => a.uri);
      if (jobUploads && !attachList.length)
        return {
          error:
            'งานนี้ใช้รูปที่อัปโหลดไว้ด้วย ซึ่งระบบไม่ได้เก็บ — แนบรูปเดิมที่ช่องรูปอ้างอิงด้านซ้ายอีกครั้ง แล้วลองใหม่',
        };
      if (!plan.productIds.length && !plan.files.length)
        return {
          error: jobMode
            ? 'ไม่พบรูปสินค้าที่งานนี้ใช้แล้ว (สินค้าอาจถูกเอาออกจากแคมเปญ) — ใช้แผงด้านซ้ายสร้างใหม่แทน'
            : 'ติ๊กเลือกรูปสินค้าที่แผงด้านซ้ายอย่างน้อย 1 รูปก่อน',
        };
      if (plan.files.length && meta.supports_mixed_refs !== 'Y')
        return { error: 'ระบบยังส่งรูปสินค้าพร้อมรูปอัปโหลดไม่ได้ — เอารูปอัปโหลดออกก่อน' };
    } else if (plan.mode === 'upload') {
      plan.files = attachList.map((a) => a.uri);
      if (!plan.files.length)
        return {
          error: jobMode
            ? 'งานนี้ใช้รูปที่อัปโหลดไว้ ซึ่งระบบไม่ได้เก็บ — แนบรูปเดิมที่ช่องรูปอ้างอิงด้านซ้ายอีกครั้ง แล้วลองใหม่'
            : 'ใส่รูปอ้างอิงที่แผงด้านซ้ายก่อน',
        };
    }
    const sourceN =
      plan.mode === 'products'
        ? plan.productIds.length + plan.files.length
        : plan.mode === 'upload'
        ? plan.files.length
        : plan.mode === 'none'
        ? 0
        : 1;
    plan.count = sourceN + (plan.mascot ? 1 : 0) + 1; // + ผังโลโก้
    const prov = regenProvider(row);
    plan.provider = prov;
    plan.max = Number(prov && prov.max_images) || meta.max_attach || 16;
    if (plan.count > plan.max)
      return {
        error: `รูปเกิน ${plan.max} รูป (นับโลโก้และมาสคอตด้วย) — เอารูปที่แผงด้านซ้ายออกก่อน`,
      };
    const what =
      plan.mode === 'products'
        ? `รูปสินค้า ${plan.productIds.length} รูป${
            plan.files.length ? ` + รูปอัปโหลด ${plan.files.length} รูป` : ''
          }`
        : plan.mode === 'upload'
        ? `รูปอัปโหลด ${plan.files.length} รูป`
        : plan.mode === 'current'
        ? 'ภาพหลักแคมเปญ'
        : 'ไม่ใช้รูปสินค้า';
    plan.note = `รูปอ้างอิงที่ส่ง: ${what}${plan.mascot ? ' + มาสคอต' : ''} + ผังโลโก้ · ${
      plan.fromJob ? 'ตามงานเดิม' : 'จากแผงด้านซ้ายตอนนี้'
    }`;
    return plan;
  };
  const regenLogo = (row, v) => {
    if (!row || !v || placing) return;
    if (!canGenerate) {
      showNotify(errorText(costBlock) || 'บัญชีนี้สร้างภาพ AI ไม่ได้', 'warning');
      return;
    }
    if (hasPending || attaching) {
      showNotify('รอภาพที่กำลังสร้างเสร็จก่อน แล้วค่อยสร้างใหม่', 'warning');
      return;
    }
    const plan = regenPlan(row);
    if (plan.error) {
      showNotify(plan.error, 'warning');
      return;
    }
    const mascot = plan.mascot ? mascotCode || ((meta.mascots || [])[0] || {}).code || '' : '';
    /* server ต่อประโยคมาสคอต + ผังตำแหน่งให้ — นับรวมก่อนส่ง เพดานเดียวกับปุ่มสร้าง */
    const sent = [
      plan.prompt,
      mascot ? meta.mascot_prompt || '' : '',
      layoutText(v.pos, [v.x, v.y], v.size, row.aspect_ratio || '1:1', logoAR),
    ].join(' ');
    if (promptLength(sent) > 2000) {
      showNotify(
        'คำสั่งของงานนี้รวมกับคำสั่งโลโก้ยาวเกิน 2,000 ตัวอักษร ใช้แผงด้านซ้ายสร้างใหม่แทน',
        'warning'
      );
      return;
    }
    setPlacing(true);
    FetchData('PUT', `/${mod}`, app.token, {
      campaign_id: id,
      product_ids: plan.mode === 'products' ? plan.productIds : [],
      logo: v.logo,
      logo_pos: v.pos,
      logo_size: v.size,
      logo_x: v.x,
      logo_y: v.y,
      mascot,
      prompt: plan.prompt,
      resolution: row.resolution || '1K',
      aspect_ratio: row.aspect_ratio || '1:1',
      source: plan.mode === 'products' && plan.files.length ? 'refs' : plan.mode,
      provider_code: plan.provider ? plan.provider.code : '',
      filedatas: plan.files,
      _t: Date.now(),
    })
      .then((result) => {
        if (!alive.current) return;
        setPlacing(false);
        if (result.status === 'ok') {
          setPlaceRow(null);
          showNotify(
            'ส่งงานสร้างใหม่พร้อมโลโก้แล้ว ภาพจะขึ้นในผลงานภายใน 2–5 นาที (ภาพเดิมยังอยู่)',
            'success'
          );
          setLoading(true); // ล็อกปุ่มจนรายการใหม่ (งานค้าง) มาถึง
          loadList();
        } else {
          showNotify(errorText(result.msg) || t(`alert.saveerror`), 'error');
        }
      })
      .catch(() => {
        if (alive.current) {
          setPlacing(false);
          showNotify('ส่งงานไม่สำเร็จ ลองใหม่อีกครั้ง', 'error');
        }
      });
  };
  const regenCredits = (row) => creditOf(regenProvider(row), (row && row.resolution) || '1K');
  const regenMode = meta.supports_logo_layout === 'Y';

  /* กรอบส่วน — เส้นบางไม่มีเงา โทนทางการ (ดีไซน์เดียวกับแท็บข้อมูล AI) */
  const sectionSx = {
    border: '1px solid',
    borderColor: 'grey.300',
    borderRadius: 2,
    p: 3,
    bgcolor: '#ffffff',
  };

  const eyebrow = (text) => (
    <MDTypography
      variant='caption'
      fontWeight='bold'
      color='text'
      sx={{ letterSpacing: 1.5, textTransform: 'uppercase', opacity: 0.7 }}
    >
      {text}
    </MDTypography>
  );

  return (
    <MDBox pt={1}>
      <MDBox
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(340px, 390px) minmax(0, 1fr)' },
          gap: 3,
          alignItems: 'start',
        }}
      >
        <MDBox
          component='section'
          aria-label='แผงกราฟิกแคมเปญ'
          sx={{
            ...sectionSx,
            p: 2,
            pb: 0,
            position: { lg: 'sticky' },
            top: 96,
            maxHeight: { lg: 'calc(100vh - 112px)' },
            overflowY: { lg: 'auto' },
          }}
        >
          <MDBox display='flex' justifyContent='space-between' alignItems='center' mb={1}>
            <MDTypography variant='h6'>กราฟิกแคมเปญ</MDTypography>
            {canEdit && (
              <MDButton
                size='small'
                color='info'
                variant='outlined'
                onClick={() => setStyleOpen(true)}
              >
                <Icon>palette</Icon>&nbsp;สไตล์
              </MDButton>
            )}
          </MDBox>
          {/* ไม่มีสิทธิ์สร้าง = ซ่อนฟอร์มทั้งแผง เหลือป้ายบอกเหตุผล (18 ต.ค. 2569 · เดิมกรอกได้ทั้งแผงแล้วค่อยเจอปุ่มเทา) */}
          {meta.can_generate === 'N' && (
            <MDBox
              role='status'
              display='flex'
              gap={1}
              alignItems='flex-start'
              p={1.5}
              mb={2}
              sx={{ bgcolor: 'grey.100', borderRadius: 1 }}
            >
              <Icon color='info'>visibility</Icon>
              <MDTypography variant='button' fontWeight='regular'>
                บัญชีนี้ยังสร้างภาพ AI ไม่ได้ · ขอสิทธิ์จากผู้ดูแลระบบ
              </MDTypography>
            </MDBox>
          )}
          {canEdit && (
            <>
              {selectedPreset && (
                <MDTypography variant='caption' color='text' display='block' mb={1}>
                  สไตล์: {selectedPreset.name}
                </MDTypography>
              )}
              <MDBox mb={1} />
              <MDBox display='flex' flexDirection='column' gap={2}>
                <MDInput
                  label='ข้อความหลัก'
                  fullWidth
                  value={form.headline}
                  onChange={(e) => setForm({ ...form, headline: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
                <MDInput
                  label='ข้อความรอง (ถ้ามี)'
                  fullWidth
                  value={form.sub}
                  onChange={(e) => setForm({ ...form, sub: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </MDBox>
              <MDBox display='flex' flexWrap='wrap' alignItems='center' mt={0.5}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size='small'
                      checked={form.period === 'Y'}
                      onChange={(e) => setForm({ ...form, period: e.target.checked ? 'Y' : 'N' })}
                    />
                  }
                  label={<MDTypography variant='caption'>ใส่ช่วงเวลาแคมเปญ</MDTypography>}
                />
                <MDButton
                  size='small'
                  variant='text'
                  color='dark'
                  onClick={requestResetForm}
                  disabled={!campaign}
                >
                  ดึงข้อความจากแคมเปญใหม่
                </MDButton>
              </MDBox>
              <Divider sx={{ my: 1.5 }} />
              <MDTypography variant='caption' fontWeight='bold'>
                รูปอ้างอิง · {referenceCount}/{maxAttach}
              </MDTypography>
              <MDInput
                label='แหล่งรูป'
                select
                fullWidth
                value={mode}
                onChange={(e) => setMode(e.target.value)}
                sx={{ mt: 1.5, mb: 1.5 }}
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value='products'>รูปสินค้าในแคมเปญ</MenuItem>
                <MenuItem value='upload'>อัปโหลดรูปเอง</MenuItem>
                <MenuItem value='current'>รูปหลักของแคมเปญ</MenuItem>
                <MenuItem value='none'>ไม่ใช้รูป (AI วาดเอง)</MenuItem>
              </MDInput>
              <input
                ref={fileRef}
                type='file'
                accept='image/jpeg,image/png,image/webp'
                multiple
                style={{ display: 'none' }}
                onChange={attachFile}
              />
              {(mode === 'products' || mode === 'upload') && (
                <>
                  <MDBox display='flex' flexWrap='wrap' gap={1} mb={1}>
                    {mode === 'products' &&
                      productPicked.map((img) => (
                        <MDBox key={img.product_id} position='relative' width={72}>
                          <img
                            src={img.image}
                            alt={img.sku}
                            style={{
                              width: 72,
                              height: 64,
                              objectFit: 'contain',
                              borderRadius: 6,
                              border: '1px solid #d0d4dc',
                            }}
                          />
                          <MDButton
                            size='small'
                            iconOnly
                            color='dark'
                            variant='contained'
                            aria-label={`เอารูป ${img.sku} ออก`}
                            onClick={() => toggleProduct(img.product_id)}
                            sx={{
                              position: 'absolute',
                              top: 0,
                              right: 0,
                              minWidth: 24,
                              width: 24,
                              height: 24,
                            }}
                          >
                            <Icon>close</Icon>
                          </MDButton>
                          <MDTypography variant='caption' display='block' sx={{ fontSize: 10 }}>
                            {img.sku}
                          </MDTypography>
                        </MDBox>
                      ))}
                    {attachList.map((a, index) => (
                      <MDBox key={a.id} position='relative' width={72}>
                        <img
                          src={a.uri}
                          alt={`อัปโหลด ${index + 1}`}
                          style={{
                            width: 72,
                            height: 64,
                            objectFit: 'contain',
                            borderRadius: 6,
                            border: '1px solid #d0d4dc',
                          }}
                        />
                        <MDButton
                          size='small'
                          iconOnly
                          color='dark'
                          variant='contained'
                          aria-label={`เอารูปอัปโหลด ${index + 1} ออก`}
                          onClick={() => removeAttach(a.id)}
                          sx={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            minWidth: 24,
                            width: 24,
                            height: 24,
                          }}
                        >
                          <Icon>close</Icon>
                        </MDButton>
                        <MDTypography variant='caption' display='block' sx={{ fontSize: 10 }}>
                          อัปโหลด {index + 1}
                        </MDTypography>
                      </MDBox>
                    ))}
                    <MDButton
                      size='small'
                      color='info'
                      variant='outlined'
                      disabled={attaching || sourceCount >= sourceLimit}
                      onClick={() => fileRef.current.click()}
                      sx={{ width: 72, minWidth: 72, height: 64 }}
                    >
                      {attaching ? 'กำลังอ่าน…' : '+ เพิ่มรูป'}
                    </MDButton>
                  </MDBox>
                  {mode === 'products' && (
                    <MDButton
                      size='small'
                      variant='text'
                      color='info'
                      onClick={() => {
                        setPickerTarget('main');
                        setProductOpen(true);
                      }}
                    >
                      เลือกสินค้าในแคมเปญ ({productPicked.length}/{productImages.length})
                    </MDButton>
                  )}
                </>
              )}
              {mode === 'current' && (
                <MDTypography variant='caption'>ใช้รูปหลักที่ตั้งไว้ของแคมเปญ</MDTypography>
              )}
              {mode === 'none' && (
                <MDTypography variant='caption'>AI วาดสินค้าเอง อาจไม่เหมือนของจริง</MDTypography>
              )}
              {(meta.logos || []).length > 0 && (
                <MDBox mt={1.5} p={1} sx={{ bgcolor: 'grey.100', borderRadius: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        size='small'
                        checked={logoOn}
                        onChange={(e) => setLogoOn(e.target.checked)}
                      />
                    }
                    label={<MDTypography variant='caption'>ใส่โลโก้ร้าน</MDTypography>}
                  />
                  {logoOn && (
                    <MDBox display='flex' gap={1} alignItems='center'>
                      {logoSelected && (
                        <img
                          src={logoSelected.image}
                          alt={logoSelected.name}
                          style={{ width: 36, height: 36, objectFit: 'contain' }}
                        />
                      )}
                      <MDInput
                        label='โลโก้ร้าน'
                        select
                        fullWidth
                        size='small'
                        value={logoCode}
                        onChange={(e) => setLogoCode(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      >
                        {(meta.logos || []).map((l) => (
                          <MenuItem key={l.code} value={l.code}>
                            {l.name}
                          </MenuItem>
                        ))}
                      </MDInput>
                    </MDBox>
                  )}
                  {logoOn && meta.supports_logo_layout === 'Y' && (
                    <MDBox display='flex' gap={1} mt={1}>
                      <MDInput
                        label='ตำแหน่งโลโก้'
                        select
                        fullWidth
                        size='small'
                        value={logoPos}
                        onChange={(e) => {
                          setLogoPos(e.target.value);
                          if (e.target.value === 'custom') setPaperOpen(true);
                        }}
                        InputLabelProps={{ shrink: true }}
                        title='AI เจนโลโก้ลงในภาพตรงตำแหน่งนี้ ตามผังที่แนบให้'
                      >
                        {(meta.logo_positions || []).map((p) => (
                          <MenuItem key={p.code} value={p.code}>
                            {p.name}
                          </MenuItem>
                        ))}
                      </MDInput>
                      {logoPos !== 'ai' && (
                        <MDInput
                          label='ขนาด'
                          select
                          size='small'
                          value={logoSize}
                          onChange={(e) => setLogoSize(Number(e.target.value))}
                          InputLabelProps={{ shrink: true }}
                          sx={{ minWidth: 96 }}
                        >
                          {/* ขนาดที่ลาก/เลื่อนเองในหน้าต่างกระดาษ (ไม่ตรง 3 ขนาด) โชว์เป็นตัวเลือกเพิ่ม */}
                          {[
                            ...(meta.logo_sizes || []),
                            ...((meta.logo_sizes || []).some((s) => s.value === Number(logoSize))
                              ? []
                              : [{ value: Number(logoSize), name: `${Number(logoSize)}%` }]),
                          ].map((s) => (
                            <MenuItem key={s.value} value={s.value}>
                              {s.name}
                            </MenuItem>
                          ))}
                        </MDInput>
                      )}
                    </MDBox>
                  )}
                  {/* กระดาษย่อตามสัดส่วนที่เลือก โชว์ว่าโลโก้จะอยู่ตรงไหน · กดเพื่อเลือกจุดเองแบบ Canva (26 ต.ค. 2569) */}
                  {logoOn &&
                    logoSelected &&
                    logoLayout &&
                    (() => {
                      const ir = ratioHW(ratio);
                      const pw = Math.min(120, 72 / ir);
                      const at =
                        logoPos === 'custom'
                          ? logoXYc
                          : presetXY(logoPos, Number(logoSize), logoAR, ir);
                      const posName =
                        ((meta.logo_positions || []).find((p) => p.code === logoPos) || {}).name ||
                        '';
                      return (
                        <MDBox mt={1} display='flex' alignItems='center' gap={1.5}>
                          <MDBox
                            component='button'
                            type='button'
                            onClick={() => setPaperOpen(true)}
                            aria-label={`เลือกจุดวางโลโก้บนกระดาษ ${ratio}`}
                            title='กดเพื่อเลือกจุดวางโลโก้'
                            sx={{
                              position: 'relative',
                              flex: 'none',
                              width: pw,
                              height: pw * ir,
                              p: 0,
                              border: '1px solid #d2d6da',
                              borderRadius: 1,
                              bgcolor: '#fff',
                              backgroundImage:
                                'linear-gradient(to right, rgba(41,52,122,.10) 1px, transparent 1px), linear-gradient(to bottom, rgba(41,52,122,.10) 1px, transparent 1px)',
                              backgroundSize: '33.333% 33.333%',
                              cursor: 'pointer',
                              overflow: 'hidden',
                            }}
                          >
                            <img
                              src={logoSelected.image}
                              alt=''
                              onLoad={(e) =>
                                setLogoAR(e.target.naturalHeight / e.target.naturalWidth || 1)
                              }
                              style={{
                                position: 'absolute',
                                left: `${at[0]}%`,
                                top: `${at[1]}%`,
                                width: `${paperBox.w}%`,
                                display: 'block',
                              }}
                            />
                          </MDBox>
                          <MDBox minWidth={0}>
                            <MDTypography variant='caption' color='text' display='block'>
                              {posName} · กระดาษ {ratio}
                            </MDTypography>
                            <MDButton
                              size='small'
                              variant='outlined'
                              color='dark'
                              onClick={() => setPaperOpen(true)}
                              sx={{ mt: 0.5 }}
                            >
                              เลือกจุดบนกระดาษ
                            </MDButton>
                          </MDBox>
                        </MDBox>
                      );
                    })()}
                </MDBox>
              )}
              {(meta.mascots || []).length > 0 && (
                <MDBox mt={1} p={1} sx={{ bgcolor: 'grey.100', borderRadius: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        size='small'
                        checked={mascotOn}
                        onChange={(e) => setMascotOn(e.target.checked)}
                      />
                    }
                    label={<MDTypography variant='caption'>ใส่มาสคอตช้างแดง</MDTypography>}
                  />
                  {mascotOn && (
                    <MDBox display='flex' gap={1} alignItems='center'>
                      {mascotSelected && (
                        <img
                          src={mascotSelected.image}
                          alt={`มาสคอต ${mascotSelected.name}`}
                          style={{ width: 36, height: 36, objectFit: 'contain' }}
                        />
                      )}
                      {(meta.mascots || []).length > 1 ? (
                        <MDInput
                          label='ท่าทาง'
                          select
                          fullWidth
                          size='small'
                          value={mascotCode}
                          onChange={(e) => setMascotCode(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                        >
                          {(meta.mascots || []).map((m) => (
                            <MenuItem key={m.code} value={m.code}>
                              {m.name}
                            </MenuItem>
                          ))}
                        </MDInput>
                      ) : (
                        <MDTypography variant='caption' color='text'>
                          {mascotSelected ? mascotSelected.name : ''} · AI ใช้ตัวจริงจากรูปนี้
                        </MDTypography>
                      )}
                    </MDBox>
                  )}
                </MDBox>
              )}
              {tooManyRefs && (
                <MDTypography variant='caption' color='error' display='block' mt={1} role='alert'>
                  รูปเกิน {maxAttach} รูป (นับโลโก้และมาสคอตด้วย) — เอารูปออกก่อน
                </MDTypography>
              )}
              <MDBox mt={2}>
                <MDInput
                  label='อยากให้มีอะไรเพิ่มในภาพ (ถ้ามี)'
                  fullWidth
                  multiline
                  rows={2}
                  value={form.extra}
                  onChange={(e) => setForm({ ...form, extra: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </MDBox>
              <Grid container spacing={1.5} mt={0.5}>
                <Grid item xs={12}>
                  <MDInput
                    label='โมเดล'
                    fullWidth
                    select
                    value={provider}
                    onChange={(e) => setProvider(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  >
                    <MenuItem value='auto' title='ระบบเลือก GPT Image 2.5 Flare ให้'>
                      ✦ อัตโนมัติ (แนะนำ)
                    </MenuItem>
                    {providerOptions.map((p) => (
                      <MenuItem key={p.code} value={p.code}>
                        {p.name} · {creditOf(p, resolution)} เครดิต
                      </MenuItem>
                    ))}
                  </MDInput>
                </Grid>
                <Grid item xs={6}>
                  <MDInput
                    label='สัดส่วนภาพ'
                    fullWidth
                    select
                    value={ratio}
                    onChange={(e) => setRatio(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  >
                    {(meta.ratios.length ? meta.ratios : ['1:1']).map((r) => (
                      <MenuItem key={r} value={r}>
                        {r}
                        {RATIO_LABELS[r] ? ` · ${RATIO_LABELS[r]}` : ''}
                      </MenuItem>
                    ))}
                  </MDInput>
                </Grid>
                <Grid item xs={6}>
                  <MDInput
                    label='ความละเอียด'
                    fullWidth
                    select
                    value={resolution}
                    onChange={(e) => setResolution(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  >
                    {(meta.resolutions.length ? meta.resolutions : ['1K', '2K']).map((r) => (
                      <MenuItem key={r} value={r}>
                        {r}
                      </MenuItem>
                    ))}
                  </MDInput>
                </Grid>
              </Grid>
              {/* ตัวนับขึ้นเฉพาะตอนใกล้เต็ม — ปกติไม่มีใครพิมพ์ถึง (รอบตัดคำ) */}
              {promptLength(fullPrompt()) > 1800 && (
                <MDTypography
                  variant='caption'
                  color={promptLength(fullPrompt()) > 2000 ? 'error' : 'warning'}
                  display='block'
                  mt={1}
                >
                  คำสั่งยาว {promptLength(fullPrompt())}/2000 ตัวอักษร
                  {promptLength(fullPrompt()) > 2000 ? ' — ลดข้อความให้สั้นลง' : ''}
                </MDTypography>
              )}
              {promptStale && (
                <MDBox mt={1} role='alert'>
                  <MDTypography variant='caption' color='warning' display='block'>
                    ตัวเลือกเปลี่ยนหลังแก้คำสั่งเอง
                  </MDTypography>
                  <MDBox display='flex' flexWrap='wrap' gap={1}>
                    <MDButton
                      size='small'
                      variant='text'
                      color='info'
                      onClick={keepPrompt}
                      sx={{ px: 0 }}
                    >
                      ใช้คำสั่งที่แก้เองต่อ
                    </MDButton>
                    <MDButton
                      size='small'
                      variant='text'
                      color='dark'
                      onClick={() => setPromptOpen(true)}
                    >
                      ดูคำสั่ง
                    </MDButton>
                  </MDBox>
                </MDBox>
              )}
              {mixedUnsupported && (
                <MDTypography variant='caption' color='warning' display='block' mt={1}>
                  แหล่งรูปแบบนี้ยังใช้ไม่ได้ เลือกแหล่งรูปอื่น
                </MDTypography>
              )}
              {noSource && (
                <MDTypography variant='caption' color='warning' display='block' mt={1}>
                  เลือกรูปอย่างน้อย 1 รูป
                </MDTypography>
              )}
              <MDButton
                size='small'
                variant='text'
                color='info'
                onClick={() => setPromptOpen(true)}
                sx={{ mt: 1, px: 0 }}
              >
                ดู/แก้คำสั่งที่ส่งให้ AI {advanced ? '(แก้เอง)' : ''}
              </MDButton>
              {/* ปุ่มสร้างติดท้ายแผงตลอด ไม่ต้องเลื่อนหาในแผงที่เลื่อนได้ (รอบตัดคำ) */}
              <MDBox
                sx={{
                  position: { lg: 'sticky' },
                  bottom: 0,
                  bgcolor: '#fff',
                  mx: -2,
                  px: 2,
                  pt: 1.5,
                  pb: 2,
                  mt: 1,
                  borderTop: '1px solid',
                  borderColor: 'grey.200',
                  zIndex: 1,
                }}
              >
                <MDButton
                  fullWidth
                  variant='gradient'
                  color='info'
                  onClick={generate}
                  disabled={
                    !canGenerate ||
                    promptLength(fullPrompt()) > 2000 ||
                    loading ||
                    hasPending ||
                    tooManyRefs ||
                    mixedUnsupported ||
                    noSource ||
                    promptStale ||
                    attaching
                  }
                >
                  <Icon>auto_awesome</Icon>&nbsp;
                  {hasPending ? 'กำลังสร้าง…' : `สร้างภาพ · ${estCredits} เครดิต`}
                </MDButton>
                {costBlock && (
                  <MDTypography variant='caption' color='error' display='block' mt={1} role='alert'>
                    {errorText(costBlock)}
                  </MDTypography>
                )}
                {hasPending && (
                  <MDBox mt={1} display='flex' gap={1} alignItems='center'>
                    <CircularProgress size={14} />
                    <MDTypography variant='caption'>กำลังสร้าง ประมาณ 2–5 นาที</MDTypography>
                  </MDBox>
                )}
                {usage && (
                  <MDBox mt={1}>
                    {usage.quota > 0 && (
                      <MDProgress
                        value={Math.min(
                          100,
                          (Number(usage.month_images) / Number(usage.quota)) * 100
                        )}
                        color='info'
                      />
                    )}
                    <MDTypography variant='caption' color='text' display='block' mt={0.5}>
                      เดือนนี้ทั้งร้าน {usage.month_images}
                      {usage.quota > 0 ? `/${usage.quota}` : ''} ภาพ
                      {usage.my_images != null ? ` · คุณ ${usage.my_images}` : ''}
                    </MDTypography>
                  </MDBox>
                )}
              </MDBox>
            </>
          )}
        </MDBox>
        <MDBox minWidth={0}>
          {/* ── ผลงาน ───────────────────────────────────────────────────────── */}
          <MDBox mb={1.5} display='flex' alignItems='center' gap={1} flexWrap='wrap'>
            {eyebrow('ผลงาน')}
            <MDTypography variant='caption' color='text' sx={{ opacity: 0.6 }}>
              {list.length > 0 ? `${list.length} รายการ` : ''}
            </MDTypography>
            {canViewAll && (
              <FormControlLabel
                sx={{ ml: 0.5 }}
                control={
                  <Switch
                    size='small'
                    checked={scopeAll}
                    onChange={(e) => {
                      scopeRef.current = e.target.checked;
                      setScopeAll(e.target.checked);
                      dl.clear();
                      setLoading(true);
                      loadList();
                    }}
                  />
                }
                label={<MDTypography variant='caption'>ดูของทุกคน</MDTypography>}
              />
            )}
            <MDBox flexGrow={1} />
            <SelectBar
              on={dl.on}
              onToggle={dl.toggleMode}
              selected={dl.count}
              total={dlAll.length}
              onAll={() => dl.addAll(dlAll)}
              onNone={dl.clear}
              onDownload={() => dl.run(`${dlName}_กราฟิก`, showNotify)}
              busy={dl.busy}
              progress={dl.progress}
            />
          </MDBox>

          {list.length === 0 ? (
            <MDBox
              py={6}
              display='flex'
              flexDirection='column'
              alignItems='center'
              gap={1}
              sx={{ border: '1px dashed', borderColor: 'grey.400', borderRadius: 2 }}
            >
              <Icon sx={{ fontSize: 40, opacity: 0.3 }}>image</Icon>
              <MDTypography variant='body2' color='text' sx={{ opacity: 0.7 }}>
                {!listReady
                  ? 'กำลังโหลดภาพ…'
                  : scopeAll
                  ? 'แคมเปญนี้ยังไม่มีภาพ'
                  : canEdit
                  ? 'คุณยังไม่ได้สร้างภาพในแคมเปญนี้ — เลือกสไตล์แล้วกดสร้างภาพ'
                  : canViewAll
                  ? 'คุณยังไม่ได้สร้างภาพในแคมเปญนี้ — เปิด "ดูของทุกคน" เพื่อดูภาพของคนอื่น'
                  : 'บัญชีนี้เห็นเฉพาะภาพที่ตัวเองสร้าง — ขอสิทธิ์ "ดู/ตรวจรับงาน AI ของทุกคน" จากผู้ดูแลระบบเพื่อดูภาพของคนอื่น'}
              </MDTypography>
            </MDBox>
          ) : (
            <Grid container spacing={2.5}>
              {list.map((row) => {
                const working = row.status === 'PENDING' || row.status === 'RUNNING';
                const done = row.status === 'SUCCESS';
                const failed = row.status === 'FAILED';
                const saved = row.approved === 'Y';
                /* บัญชีดูอย่างเดียวไม่มีปุ่มบนการ์ด ยกเว้นทางลัดไปแท็บรูปภาพ (18 ต.ค. 2569) และดาวน์โหลด (25 ต.ค. 2569) */
                const hasActions =
                  canEdit || Boolean(saved && onOpenImages) || Boolean(row.image_url);
                /* ป้ายบนรูปขึ้นเฉพาะสถานะที่ต้องรู้ (18 ต.ค. 2569 · แบบเดียวกับหน้าภาพสินค้า AI) */
                let badge = null;
                if (saved)
                  badge = <Chip size='small' color='success' label='อยู่ในรูปแคมเปญแล้ว' />;
                else if (!working && !done) {
                  const st = statusList[row.status] || { name: row.status, color: 'default' };
                  badge = (
                    <Chip
                      size='small'
                      color={st.color === 'default' ? undefined : st.color}
                      label={st.name}
                    />
                  );
                }

                return (
                  <Grid item xs={12} sm={6} key={row.id}>
                    <Card
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        border: '1px solid',
                        borderColor: 'grey.300',
                        boxShadow: 'none',
                        overflow: 'hidden',
                      }}
                    >
                      <ReviewThumb
                        onReview={row.image_url ? () => setReviewId(row.id) : null}
                        label={`ตรวจภาพงาน ${row.id}`}
                        select={
                          row.image_url
                            ? dl.selectProps(dlItem(row), `เลือกภาพงาน ${row.id}`)
                            : null
                        }
                        badge={badge}
                        meta={[
                          row.image_url
                            ? `${row.resolution}${
                                row.credits_used > 0 ? ` · ${row.credits_used} เครดิต` : ''
                              }`
                            : '',
                          scopeAll ? row.created_by_name || 'ระบบ' : '',
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                        sx={{
                          aspectRatio: '4 / 3',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'grey.100',
                        }}
                      >
                        {row.image_url ? (
                          <img
                            src={row.image_url}
                            alt={`cg-${row.id}`}
                            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                          />
                        ) : working ? (
                          <MDBox display='flex' flexDirection='column' alignItems='center' gap={1}>
                            <CircularProgress size={28} color='inherit' sx={{ opacity: 0.4 }} />
                            <MDTypography variant='caption' color='text' sx={{ opacity: 0.6 }}>
                              กำลังสร้าง…
                            </MDTypography>
                          </MDBox>
                        ) : (
                          <Icon sx={{ fontSize: 36, opacity: 0.25 }}>broken_image</Icon>
                        )}
                      </ReviewThumb>

                      {!working && (failed || hasActions) && (
                        <MDBox p={1.25} display='flex' flexDirection='column' flexGrow={1}>
                          {failed && (
                            <MDTypography
                              variant='caption'
                              color='error'
                              display='block'
                              mb={hasActions ? 1 : 0}
                            >
                              {failReason(row.error_msg)}
                            </MDTypography>
                          )}
                          {hasActions && (
                            <ActionRow>
                              {done && !saved && canEdit ? (
                                <MDButton
                                  size='small'
                                  variant='gradient'
                                  color='info'
                                  onClick={() => approve(row)}
                                  data-primary=''
                                  sx={{ flexGrow: 1, minWidth: 0, px: 1 }}
                                >
                                  <Icon>collections</Icon>&nbsp;เก็บเข้ารูปแคมเปญ
                                </MDButton>
                              ) : failed && canEdit ? (
                                <MDButton
                                  size='small'
                                  variant='outlined'
                                  color='info'
                                  onClick={() => retryFrom(row)}
                                  data-primary=''
                                  sx={{ flexGrow: 1, minWidth: 0, px: 1 }}
                                >
                                  <Icon>refresh</Icon>&nbsp;ลองใหม่
                                </MDButton>
                              ) : saved && onOpenImages ? (
                                <MDButton
                                  size='small'
                                  variant='outlined'
                                  color='dark'
                                  onClick={onOpenImages}
                                  data-primary=''
                                  sx={{ flexGrow: 1, minWidth: 0, px: 1 }}
                                >
                                  <Icon>photo_library</Icon>&nbsp;เปิดรูปแคมเปญ
                                </MDButton>
                              ) : (
                                <MDBox flexGrow={1} />
                              )}
                              {done && canEdit && (
                                <MDButton
                                  size='small'
                                  iconOnly
                                  variant='outlined'
                                  color='dark'
                                  title='แก้ภาพนี้ (สร้างรอบใหม่)'
                                  aria-label={`แก้ไขภาพงาน ${row.id}`}
                                  sx={iconBtnSx}
                                  disabled={!canGenerate}
                                  onClick={() => {
                                    setEditRow(row);
                                    setEditPrompt('');
                                    setEditRatio(row.aspect_ratio || '1:1');
                                    setEditResolution(row.resolution || '1K');
                                    setEditProvider('auto');
                                    setEditProductSel([]);
                                    setEditAttach([]);
                                  }}
                                >
                                  <Icon>edit</Icon>
                                </MDButton>
                              )}
                              {row.image_url && (
                                <DownloadButton
                                  label={`ดาวน์โหลดภาพงาน ${row.id}`}
                                  onClick={() =>
                                    dl.one(row.image_url, dlItem(row).name, showNotify)
                                  }
                                />
                              )}
                              <CardMenu
                                label={`ตัวเลือกเพิ่มเติมของงาน ${row.id}`}
                                items={[
                                  canEdit &&
                                    done &&
                                    Boolean(row.image_url) &&
                                    (regenMode || meta.supports_logo_overlay === 'Y') &&
                                    (meta.logos || []).length > 0 && {
                                      label: regenMode
                                        ? 'สร้างใหม่พร้อมโลโก้'
                                        : row.logo_url
                                        ? 'ย้ายโลโก้'
                                        : 'วางโลโก้ (ไม่เสียเครดิต)',
                                      icon: 'branding_watermark',
                                      onClick: () => {
                                        if (regenMode && !canGenerate) {
                                          showNotify(
                                            errorText(costBlock) || 'บัญชีนี้สร้างภาพ AI ไม่ได้',
                                            'warning'
                                          );
                                          return;
                                        }
                                        const plan = regenMode ? regenPlan(row) : {};
                                        if (plan.error) {
                                          showNotify(plan.error, 'warning');
                                          return;
                                        }
                                        setPlaceRow(row);
                                      },
                                    },
                                  canEdit && {
                                    label: 'ลบ',
                                    icon: 'delete_outline',
                                    danger: true,
                                    onClick: () => setDelRow(row),
                                  },
                                ]}
                              />
                            </ActionRow>
                          )}
                        </MDBox>
                      )}
                    </Card>
                  </Grid>
                );
              })}
              {total > list.length ? (
                <Grid item xs={12}>
                  <MDBox textAlign='center'>
                    <MDButton
                      variant='text'
                      color='info'
                      onClick={() => {
                        limitRef.current += 50;
                        loadList();
                      }}
                    >
                      แสดงเพิ่ม · {list.length} จาก {total}
                    </MDButton>
                  </MDBox>
                </Grid>
              ) : null}
            </Grid>
          )}
        </MDBox>
      </MDBox>

      <Dialog open={resetConfirm} onClose={() => setResetConfirm(false)} fullWidth maxWidth='xs'>
        <DialogTitle>แทนที่ข้อความที่แก้ไว้?</DialogTitle>
        <DialogContent>
          <MDTypography variant='body2'>ข้อความที่แก้ไว้จะถูกแทนด้วยข้อความจากแคมเปญ</MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton variant='outlined' color='secondary' onClick={() => setResetConfirm(false)}>
            ใช้ข้อความที่แก้ไว้ต่อ
          </MDButton>
          <MDButton variant='gradient' color='dark' onClick={resetForm}>
            ดึงข้อความใหม่
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* ── dialog แก้ไข — สร้างรอบใหม่จากภาพเดิม ─────────────────────── */}
      <Dialog open={Boolean(editRow)} onClose={() => setEditRow(null)} fullWidth maxWidth='sm'>
        <DialogTitle>แก้ไขภาพ (สร้างรอบใหม่จากภาพนี้)</DialogTitle>
        <DialogContent>
          <MDBox mt={1}>
            {/* ปุ่มแก้สำเร็จรูป (5 ต.ค. 2569) — กดแล้วเติมข้อความให้ ไม่ต้องพิมพ์เอง กดหลายปุ่มต่อกันได้ */}
            <MDBox display='flex' flexWrap='wrap' gap={0.75} mb={1.5}>
              {QUICK_FIXES.map((q) => (
                <Chip
                  key={q.label}
                  size='small'
                  variant='outlined'
                  label={q.label}
                  onClick={() => setEditPrompt((prev) => (prev ? `${prev} ${q.text}` : q.text))}
                />
              ))}
            </MDBox>
            <MDInput
              label='อยากแก้อะไรในภาพนี้'
              placeholder='เช่น เปลี่ยนพื้นหลังเป็นสีชมพูอ่อน เพิ่มเงาใต้ขวด'
              fullWidth
              multiline
              rows={3}
              value={editPrompt}
              onChange={(e) => setEditPrompt(e.target.value)}
              error={promptLength(editPrompt) > 2000}
              helperText={
                promptLength(editPrompt) > 1800
                  ? `${promptLength(editPrompt)}/2000 ตัวอักษร${
                      promptLength(editPrompt) > 2000 ? ' — ลดข้อความให้สั้นลง' : ''
                    }`
                  : ''
              }
              InputLabelProps={{ shrink: true }}
            />
            {meta.supports_edit_refs === 'Y' && (
              <MDBox mt={2}>
                <MDTypography variant='caption' fontWeight='bold' display='block' mb={0.5}>
                  รูปอ้างอิง {editCount + 1}/{editLimit + 1} · ภาพเดิม + รูปที่แนบเพิ่ม
                </MDTypography>
                {editCount > editLimit && (
                  <MDTypography variant='caption' color='error' display='block' mb={0.5}>
                    เกินที่โมเดลนี้รับ — เอารูปออก {editCount - editLimit} รูป
                  </MDTypography>
                )}
                <input
                  ref={editFileRef}
                  type='file'
                  accept='image/jpeg,image/png,image/webp'
                  multiple
                  style={{ display: 'none' }}
                  onChange={attachEditFile}
                />
                <MDBox display='flex' flexWrap='wrap' gap={1}>
                  {editRow && editRow.image_url && (
                    <MDBox width={72} title='ภาพเดิมที่จะแก้ ส่งเป็นรูปแรกเสมอ'>
                      <img
                        src={editRow.image_url}
                        alt='ภาพเดิม'
                        style={{
                          width: 72,
                          height: 64,
                          objectFit: 'contain',
                          borderRadius: 6,
                          border: '2px solid #29347A',
                        }}
                      />
                      <MDTypography variant='caption' display='block' sx={{ fontSize: 10 }}>
                        ภาพเดิม
                      </MDTypography>
                    </MDBox>
                  )}
                  {productImages
                    .filter((img) => editProductSel.includes(img.product_id))
                    .map((img) => (
                      <MDBox key={img.product_id} position='relative' width={72}>
                        <img
                          src={img.image}
                          alt={img.sku}
                          style={{
                            width: 72,
                            height: 64,
                            objectFit: 'contain',
                            borderRadius: 6,
                            border: '1px solid #d0d4dc',
                          }}
                        />
                        <MDButton
                          size='small'
                          iconOnly
                          color='dark'
                          variant='contained'
                          aria-label={`เอารูป ${img.sku} ออก`}
                          onClick={() => toggleEditProduct(img.product_id)}
                          sx={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            minWidth: 24,
                            width: 24,
                            height: 24,
                          }}
                        >
                          <Icon>close</Icon>
                        </MDButton>
                        <MDTypography variant='caption' display='block' sx={{ fontSize: 10 }}>
                          {img.sku}
                        </MDTypography>
                      </MDBox>
                    ))}
                  {editAttach.map((a, index) => (
                    <MDBox key={a.id} position='relative' width={72}>
                      <img
                        src={a.uri}
                        alt={`อัปโหลด ${index + 1}`}
                        style={{
                          width: 72,
                          height: 64,
                          objectFit: 'contain',
                          borderRadius: 6,
                          border: '1px solid #d0d4dc',
                        }}
                      />
                      <MDButton
                        size='small'
                        iconOnly
                        color='dark'
                        variant='contained'
                        aria-label={`เอารูปอัปโหลด ${index + 1} ออก`}
                        onClick={() => removeEditAttach(a.id)}
                        sx={{
                          position: 'absolute',
                          top: 0,
                          right: 0,
                          minWidth: 24,
                          width: 24,
                          height: 24,
                        }}
                      >
                        <Icon>close</Icon>
                      </MDButton>
                      <MDTypography variant='caption' display='block' sx={{ fontSize: 10 }}>
                        อัปโหลด {index + 1}
                      </MDTypography>
                    </MDBox>
                  ))}
                  <MDButton
                    size='small'
                    color='info'
                    variant='outlined'
                    disabled={editAttaching || editCount >= editLimit}
                    onClick={() => editFileRef.current.click()}
                    sx={{ width: 72, minWidth: 72, height: 64 }}
                  >
                    {editAttaching ? 'กำลังอ่าน…' : '+ เพิ่มรูป'}
                  </MDButton>
                </MDBox>
                {productImages.length > 0 && (
                  <MDButton
                    size='small'
                    variant='text'
                    color='info'
                    onClick={() => {
                      setPickerTarget('edit');
                      setProductOpen(true);
                    }}
                  >
                    เลือกสินค้าในแคมเปญ ({editProductSel.length}/{productImages.length})
                  </MDButton>
                )}
              </MDBox>
            )}
            <MDBox mt={2}>
              <MDInput
                label='โมเดล'
                fullWidth
                select
                value={editProvider}
                onChange={(e) => setEditProvider(e.target.value)}
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value='auto' title='ระบบเลือก GPT Image 2.5 Flare ให้'>
                  ✦ อัตโนมัติ (แนะนำ)
                </MenuItem>
                {editProviderOptions.map((p) => (
                  <MenuItem key={p.code} value={p.code}>
                    {p.name} · {creditOf(p, resolution)} เครดิต
                  </MenuItem>
                ))}
              </MDInput>
            </MDBox>
            <MDTypography variant='caption' color='text' display='block' mt={1}>
              {editResolution} · {editRatio} (ตามภาพนี้) · ได้ภาพใหม่อีก 1 ภาพ ภาพเดิมยังอยู่
            </MDTypography>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton variant='outlined' color='secondary' onClick={() => setEditRow(null)}>
            {t(`element.cancel`)}
          </MDButton>
          <MDButton
            variant='gradient'
            color='dark'
            onClick={saveEdit}
            disabled={
              loading ||
              !editPrompt.trim() ||
              !editPicked ||
              !canGenerate ||
              editCount > editLimit ||
              promptLength(editPrompt) > 2000
            }
          >
            <Icon>auto_awesome</Icon>&nbsp;สร้างภาพใหม่ · ≈ {editCredit ?? '?'} เครดิต
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* ── dialog ยืนยันลบ ────────────────────────────────────────────── */}
      <Dialog open={Boolean(delRow)} onClose={() => setDelRow(null)} maxWidth='xs' fullWidth>
        <DialogTitle>ลบภาพนี้ออกจากรายการ?</DialogTitle>
        <DialogContent>
          <MDTypography variant='body2' color='text'>
            ภาพจะหายจากรายการนี้
            {delRow && delRow.approved === 'Y' ? ' (ภาพในรูปแคมเปญยังอยู่)' : ''}
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton variant='outlined' color='secondary' onClick={() => setDelRow(null)}>
            {t(`element.cancel`)}
          </MDButton>
          <MDButton variant='gradient' color='error' onClick={remove}>
            <Icon>delete</Icon>&nbsp;{t(`element.delete`)}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog open={styleOpen} onClose={() => setStyleOpen(false)} fullWidth maxWidth='md'>
        <DialogTitle>เลือกสไตล์ภาพ</DialogTitle>
        <DialogContent dividers>
          {' '}
          {presetList.length ? (
            <MDBox mt={2}>
              <MDBox display='flex' flexWrap='wrap' gap={0.75}>
                <Chip
                  size='small'
                  label='ยอดนิยม'
                  color={presetCat === '' ? 'info' : 'default'}
                  variant={presetCat === '' ? 'filled' : 'outlined'}
                  onClick={() => setPresetCat('')}
                />
                {presetCategories.map((c) => (
                  <Chip
                    key={c}
                    size='small'
                    label={c}
                    color={presetCat === c ? 'info' : 'default'}
                    variant={presetCat === c ? 'filled' : 'outlined'}
                    onClick={() => setPresetCat(c)}
                  />
                ))}
              </MDBox>
              <MDBox
                mt={1.5}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                  gap: 1.5,
                }}
              >
                {presetVisible.map((p) => (
                  <ButtonBase
                    key={p.code}
                    onClick={() => applyPreset(p)}
                    aria-pressed={presetCode === p.code}
                    sx={{
                      display: 'block',
                      textAlign: 'left',
                      border: '2px solid',
                      borderColor: presetCode === p.code ? 'info.main' : 'grey.300',
                      borderRadius: 2,
                      overflow: 'hidden',
                      bgcolor: '#ffffff',
                    }}
                  >
                    {p.example ? (
                      <MDBox
                        component='img'
                        src={p.example}
                        alt={p.name}
                        sx={{ width: '100%', height: 100, objectFit: 'cover', display: 'block' }}
                      />
                    ) : (
                      <MDBox
                        sx={{
                          height: 100,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: swatchBg(p.swatch),
                        }}
                      >
                        <Icon
                          sx={{
                            fontSize: '2rem !important',
                            color: '#ffffff',
                            filter: 'drop-shadow(0 1px 2px rgba(0,0,0,.45))',
                          }}
                        >
                          {p.aspect_ratio ? 'crop' : 'palette'}
                        </Icon>
                      </MDBox>
                    )}
                    <MDBox px={1.25} py={1}>
                      <MDTypography
                        variant='button'
                        fontWeight='bold'
                        display='block'
                        sx={{ lineHeight: 1.3 }}
                      >
                        {p.name}
                      </MDTypography>
                      <MDTypography
                        variant='caption'
                        color='text'
                        display='block'
                        sx={{ lineHeight: 1.3, opacity: 0.75 }}
                      >
                        {p.description}
                      </MDTypography>
                      {usageOf(p.code) ? (
                        <MDTypography
                          variant='caption'
                          color='info'
                          display='block'
                          sx={{ mt: 0.25 }}
                        >
                          ใช้ไปแล้ว {usageOf(p.code).count} ครั้ง
                        </MDTypography>
                      ) : null}
                    </MDBox>
                  </ButtonBase>
                ))}
              </MDBox>
              {presetCat === '' && presetList.length > POPULAR_COUNT ? (
                <MDBox mt={1}>
                  <MDButton
                    variant='text'
                    color='info'
                    size='small'
                    onClick={() => setShowAll(!showAll)}
                  >
                    {showAll ? 'แสดงเฉพาะยอดนิยม' : `ดูสไตล์ทั้งหมด ${presetList.length} แบบ`}
                    &nbsp;<Icon>{showAll ? 'expand_less' : 'expand_more'}</Icon>
                  </MDButton>
                </MDBox>
              ) : null}
            </MDBox>
          ) : null}
        </DialogContent>
        <DialogActions>
          <TemplateManager scope='campaign' app={app} onChanged={loadMeta} onNotify={showNotify} />
          <MDButton color='dark' onClick={() => setStyleOpen(false)}>
            ปิดคลังสไตล์
          </MDButton>
        </DialogActions>
      </Dialog>
      {/* dialog เลือกสินค้าในแคมเปญ — ใช้ร่วมแผงหลัก (pickerTarget main) และหน้าต่างแก้ไข (edit · 25 ต.ค. 2569) */}
      <Dialog open={productOpen} onClose={() => setProductOpen(false)} fullWidth maxWidth='sm'>
        <DialogTitle>
          เลือกสินค้าในแคมเปญ ·{' '}
          {pickerTarget === 'edit' ? editProductSel.length : productPicked.length} รูป
        </DialogTitle>
        <DialogContent dividers>
          <MDBox display='flex' flexWrap='wrap' gap={2}>
            {productImages.map((img) => {
              const forEdit = pickerTarget === 'edit';
              const on = (forEdit ? editProductSel : productSel).includes(img.product_id);
              const full = forEdit ? editCount >= editLimit : sourceCount >= sourceLimit;
              return (
                <ButtonBase
                  key={img.product_id}
                  aria-label={`ใช้รูป ${img.sku}`}
                  aria-pressed={on}
                  disabled={!on && full}
                  onClick={() =>
                    forEdit ? toggleEditProduct(img.product_id) : toggleProduct(img.product_id)
                  }
                  sx={{
                    width: 88,
                    display: 'block',
                    border: '2px solid',
                    borderColor: on ? 'info.main' : 'grey.300',
                    borderRadius: 1,
                    p: 0.5,
                    opacity: on ? 1 : 0.6,
                  }}
                >
                  <img
                    src={img.image}
                    alt={img.sku}
                    style={{ width: 72, height: 72, objectFit: 'contain' }}
                  />
                  <MDTypography variant='caption' display='block'>
                    {on ? '✓ ' : ''}
                    {img.sku}
                  </MDTypography>
                </ButtonBase>
              );
            })}
          </MDBox>
          {(pickerTarget === 'edit' ? editCount >= editLimit : sourceCount >= sourceLimit) && (
            <MDTypography variant='caption' color='warning'>
              เต็มแล้ว — เอารูปอื่นออกก่อนถ้าจะเลือกเพิ่ม
            </MDTypography>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton color='dark' onClick={() => setProductOpen(false)}>
            เสร็จแล้ว
          </MDButton>
        </DialogActions>
      </Dialog>
      <Dialog
        open={promptOpen}
        onClose={() => setPromptOpen(false)}
        fullWidth
        maxWidth='md'
        aria-labelledby='campaign-prompt-title'
      >
        <DialogTitle id='campaign-prompt-title'>คำสั่งที่ส่งให้ AI</DialogTitle>
        <DialogContent dividers>
          <MDTypography variant='body2' mb={2}>
            {advanced
              ? 'กำลังใช้คำสั่งที่แก้เอง'
              : 'ระบบเขียนคำสั่งนี้ให้จากตัวเลือกในแผงอัตโนมัติ'}
          </MDTypography>
          {advanced ? (
            <MDInput
              label='คำสั่งสร้างภาพเต็ม'
              id='campaign-prompt-editor'
              fullWidth
              multiline
              rows={8}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              error={promptLength(fullPrompt()) > 2000}
              helperText={
                promptLength(fullPrompt()) > 1800
                  ? `${promptLength(fullPrompt())}/2000 ตัวอักษร${
                      promptLength(fullPrompt()) > 2000 ? ' — ลดข้อความให้สั้นลง' : ''
                    }`
                  : ''
              }
              InputLabelProps={{ shrink: true }}
            />
          ) : (
            <MDTypography variant='body2' sx={{ whiteSpace: 'pre-wrap' }}>
              {fullPrompt()}
            </MDTypography>
          )}
          {advanced &&
            ((logoOn &&
              logoCode &&
              !prompt.includes(logoLayout ? 'ผังตำแหน่งโลโก้' : meta.logo_prompt || '\u0000')) ||
              (mascotOn && mascotCode && !prompt.includes(meta.mascot_prompt || '\u0000'))) && (
              <MDTypography variant='caption' display='block'>
                ระบบเติมคำสั่งใส่โลโก้/มาสคอตให้เอง
              </MDTypography>
            )}
          {promptStale && (
            <MDBox mt={2} p={2} sx={{ bgcolor: 'grey.100' }}>
              <MDTypography variant='button' color='warning'>
                ตัวเลือกเปลี่ยนไปแล้ว — คำสั่งตามตัวเลือกล่าสุดคือ
              </MDTypography>
              <MDTypography variant='body2' sx={{ whiteSpace: 'pre-wrap' }}>
                {composePrompt()}
              </MDTypography>
              <MDButton color='info' onClick={keepPrompt} sx={{ mt: 1 }}>
                ใช้คำสั่งที่แก้เองต่อ
              </MDButton>
            </MDBox>
          )}
          <MDBox mt={1} display='flex' flexWrap='wrap' gap={1}>
            {!advanced ? (
              <MDButton color='info' onClick={() => toggleAdvanced(true)}>
                แก้คำสั่งเอง
              </MDButton>
            ) : (
              <MDButton color='info' onClick={() => setPromptReset(true)}>
                ใช้ตัวเลือกล่าสุด
              </MDButton>
            )}
            {advanced && (
              <MDButton color='dark' onClick={() => setAdvanced(false)}>
                กลับไปใช้คำสั่งอัตโนมัติ
              </MDButton>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton color='dark' onClick={() => setPromptOpen(false)}>
            ปิด
          </MDButton>
        </DialogActions>
      </Dialog>
      <Dialog open={promptReset} onClose={() => setPromptReset(false)} maxWidth='xs' fullWidth>
        <DialogTitle>แทนที่คำสั่งที่แก้เอง?</DialogTitle>
        <DialogContent>
          <MDTypography variant='body2'>
            คำสั่งที่พิมพ์เองจะถูกแทนด้วยคำสั่งจากตัวเลือกล่าสุด
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton color='dark' onClick={() => setPromptReset(false)}>
            ใช้คำสั่งที่แก้ไว้ต่อ
          </MDButton>
          <MDButton
            color='info'
            onClick={() => {
              setPrompt(stripSystem(composePrompt()));
              setPromptSnapshot(promptKey());
              setPromptReset(false);
            }}
          >
            แทนที่
          </MDButton>
        </DialogActions>
      </Dialog>

      <ImageReview
        items={list
          .filter((row) => row.image_url)
          .map((row) => ({
            id: row.id,
            title: `${(campaign && campaign.code) || `แคมเปญ #${id}`} · งาน #${row.id}`,
            src: row.image_url,
            fileName: dlItem(row).name,
            approved: row.approved === 'Y',
            canApprove: row.status === 'SUCCESS' && canEdit,
            approveLabel: 'เก็บเข้ารูปแคมเปญ',
            row,
          }))}
        selectedId={reviewId}
        onChange={setReviewId}
        onClose={() => setReviewId(null)}
        onApprove={approve}
        busy={loading}
      />
      {/* เลือกจุดวางโลโก้บนกระดาษก่อนสร้าง — รูปทรงตามสัดส่วนที่เลือก · ได้ตำแหน่งสำเร็จรูปหรือกำหนดเอง */}
      {paperOpen && logoSelected && (
        <LogoPlacer
          open
          paper={ratio}
          onClose={() => setPaperOpen(false)}
          logos={[logoSelected]}
          positions={meta.logo_positions || []}
          sizes={meta.logo_sizes || []}
          value={{
            logo: logoSelected.code,
            pos: logoPos,
            x: logoXYc[0],
            y: logoXYc[1],
            size: Number(logoSize),
          }}
          onSave={(v) => {
            setLogoPos(v.pos);
            if (v.pos === 'custom') setLogoXY([v.x, v.y]);
            setLogoSize(v.size);
            setPaperOpen(false);
          }}
        />
      )}
      {/* วาง/ย้ายโลโก้บนภาพที่เสร็จแล้ว (A3) — ไม่เรียก AI · โลโก้ที่แถวมีอยู่จับคู่กลับเป็นรหัสจาก URL */}
      {placeRow && (
        <LogoPlacer
          open
          onClose={() => setPlaceRow(null)}
          baseUrl={placeRow.base_url || placeRow.image_url}
          paper={regenMode ? placeRow.aspect_ratio || '1:1' : ''}
          logos={regenMode ? meta.logos || [] : placerLogos(placeRow)}
          positions={meta.logo_positions || []}
          sizes={meta.logo_sizes || []}
          regen={regenMode ? `สร้างภาพใหม่ · ${regenCredits(placeRow)} เครดิต` : ''}
          note={regenMode ? regenPlan(placeRow).note || '' : ''}
          value={{
            logo:
              regenMode && placerLogoCode(placeRow) === CURRENT_LOGO
                ? logoCode
                : placerLogoCode(placeRow) || logoCode,
            pos: placeRow.logo_pos || (regenMode ? logoPos : ''),
            x: placeRow.logo_pos ? Number(placeRow.logo_x) || 0 : regenMode ? logoXY[0] : 0,
            y: placeRow.logo_pos ? Number(placeRow.logo_y) || 0 : regenMode ? logoXY[1] : 0,
            size: Number(placeRow.logo_size) || (regenMode ? Number(logoSize) : 0),
          }}
          approved={!regenMode && placeRow.approved === 'Y'}
          busy={placing}
          onSave={(v) => (regenMode ? regenLogo(placeRow, v) : placeLogo(placeRow, v))}
          onRemove={placeRow.logo_url ? () => placeLogo(placeRow, null) : null}
        />
      )}
      {notify}

      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    </MDBox>
  );
}

CampaignGraphic.defaultProps = { onSaved: null, onOpenImages: null };
CampaignGraphic.propTypes = {
  onSaved: PropTypes.func, // เรียกหลังเก็บเข้ารูปแคมเปญสำเร็จ — Campaign.jsx โหลดแท็บรูปภาพใหม่
  onOpenImages: PropTypes.func, // ปุ่ม "เปิดรูปแคมเปญ" บนการ์ดที่เก็บแล้ว
};

export default CampaignGraphic;
